import {
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TableFooter,
  TableSortLabel,
  Box,
  FormHelperText,
  Checkbox,
  InputLabel,
  Stack,
  CircularProgress,
  Tooltip
} from '@mui/material'
import {
  Close,
  Save,
  Search,
  LibraryAddCheck,
  CheckBox,
  DeleteOutlineRounded,
  Done,
  ArrowUpwardOutlined,
  ArrowDownwardOutlined,
  ArrowDropDown,
  ArrowDropUp,
  TuneOutlined,
  InsertDriveFileRounded
} from '@mui/icons-material'
import WarningIcon from '@mui/icons-material/Warning'
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { apiGet, apiPost, apiPut } from './Api_Functions'
import AlertUI from './AlertUI'
import { SmartDialog, SmartDeleteDialog } from './utilities/SmartDialog'
import { isDateColumnType, sortTableRows } from './utilities/tableRowSorting'
import SmartMaterialUIToolbar from './SmartMateriaUIToolbar'
import { useSmartInterval } from './hooks/useSmartInterval'
import SmartMateriaUITablePagination from './SmartMaterialUITablePagination'
import moment from 'moment/moment.js'
import ParameterFilters from './SmartMaterialUITableParameterFilters'
import { usePagination } from '../generic/hooks/usePagination'
import { usePaginationMultiSelection } from './hooks/usePaginationMultiSelection'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { cellSystemValueText, hasSystemValueOptions, isSeededRelationField, multiSystemValueText, optionSystemValueText, systemValueFilterOptions } from './systemValues/tableSystemValues'
import createDialogTitle from '../generic/utilities/dialogUtil'
import SmartSelect from './smartSelect/SmartSelect'
import { useTheme } from '@emotion/react'
import { SmartAccordion } from './smartAccordion'
import useToggleSmartAccordionVisibility from './hooks/useToggleSmartAccordionVisibility/useToggleSmartAccordionVisibility'
import { DateTimePicker, DatePicker } from '@mui/x-date-pickers'
import arrayUnionNoDuplicates from '../../utils/array_union_no_duplicates'
import NonDeletableItemsDataShowAlert from './NonDeletableItems/NonDeletableItemsDataShowAlert'
import { useDateFormatter } from './hooks/useDateFormatter'
import ActionsMenu from './ActionsMenu'
import TableTune from './TableTune'
import DateColumnFilterPopover from './DateColumnFilterPopover'

const SmartTablePaper = (props) => (
  <Paper
    sx={{
      background: theme => theme.palette.background.color_373636
    }}
  >
    {props.children}
  </Paper>
)

const SmartTableRow = (props) => {
  const { selected, row, backgroundColor, onClick, children, rowChange, onMouseEnter, onMouseLeave, groupHover } = props

  const defaultSX = {
    backgroundColor: selected
      ? theme => theme.palette.background.table_row_hover
      : rowChange
        ? theme => theme.palette.background.table_row_hover
        : row
          ? theme => theme.palette.background.color_373636
          : theme => theme.palette.background.row2,
    // Rows that belong to a record group (a data row plus its secondaryLine rows)
    // are highlighted purely via the `selected` state so the whole group transitions
    // in the same render. The native `:hover` only lights up the row under the cursor,
    // which would otherwise lead the paired row by a render cycle and look laggy.
    ...(groupHover
      ? {}
      : {
          '&:hover': {
            backgroundColor: theme => theme.palette.background.table_row_hover
          }
        }),
    transition: 'background-color 0.3s ease-out'
  }

  return (
    <TableRow
      sx={backgroundColor ? { backgroundColor } : defaultSX}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </TableRow>
  )
}

const SmartTableHead = (props) => (
  <TableCell
    padding={props.padding}
    colSpan={props.colSpan}
    sx={{
      fontSize: props.compactHeader ? 14 : 15,
      py: props.compactHeader ? 0.5 : undefined,
      px: props.compactHeader ? 1 : undefined,
      borderBottom: theme => `1px solid ${theme.palette.primary.main}99`,
      borderTop: theme => `1px solid ${theme.palette.primary.main}99`,
      backgroundColor: theme => `${theme.palette.background.color_212121}95`,
      verticalAlign: 'middle',
      height: props.compactHeader ? 32 : undefined
    }}
  >
    {props.children}
  </TableCell>
)

const tableCellStyle = {
  borderBottom: '0px !important'

}
const getTableSearchStyle = (compactHeader) => ({
  '& input::placeholder': {
    fontSize: compactHeader ? '12px !important' : '13px !important'
  },
  '& .MuiInput-underline:before': {
    borderBottom: '0px !important'
  },
  '& .MuiInputBase-input': {
    padding: compactHeader ? '4px 0 3px' : undefined
  },
  fontSize: compactHeader ? '13px !important' : '14px !important',
  height: compactHeader ? 30 : undefined
})

const getTableSearchIconStyle = (compactHeader) => ({
  fontSize: compactHeader ? '16px !important' : '18px !important'
})
const textFieldUnderlineStyle = {
  '& .MuiInputBase-input': {
    color: '#979797 !important' // Text color

  },
  '& .MuiInput-underline:before': {
    borderBottomColor: 'transparent' // Semi-transparent underline
  },
  '& .MuiInput-underline:hover:before': {
    borderBottomColor: 'transparent' // Solid underline on hover
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'transparent' // Solid underline on focus
  },
  pointerEvents: 'none'
}
const textFieldStyle = {
  '&.Mui-disabled:after': {
    borderBottom: '1px dotted red',
    transform: 'scaleX(0)'
  },
  '& .MuiInput-underline.readonly': {
    borderBottomColor: 'transparent' // Solid underline on focus
  },
  '& .MuiInputBase-input::placeholder': {
    fontSize: '13px'
  },
  minWidth: '5rem'
}

const smartTableIndexKey = 'smart-table-index'
const rowHasTableIndexDefined = (row) => {
  return row && row !== undefined && row !== null && row[smartTableIndexKey] !== undefined && row[smartTableIndexKey] !== null
}

const primaryKeysDefault = ['id']
const extraFilterParameterDefault = { name: 'systemDefault', value: true }
const inheritedStickyTopOffsetCssVar = 'var(--smart-navigator-sticky-offset, 0px)'

// Column types that get the date filter popover automatically. Owned by `tableRowSorting`, which
// also sorts by them (historia 127): one list, so the filter and the order never disagree.

// Pure client-side evaluation of a date operator filter (used by non-pageable tables; pageable
// tables resolve operators server-side). `cellDate` is the already-parsed Date of the cell value.
const matchesDateOperator = (cellDate, storedValue, operator) => {
  if (!cellDate) return false
  if (operator === 'between') {
    const [fromValue, toValue] = String(storedValue).split('|')
    const from = fromValue ? new Date(fromValue) : null
    const to = toValue ? new Date(toValue) : null
    if (from && cellDate < from) return false
    if (to && cellDate > to) return false
    return true
  }
  const target = new Date(storedValue)
  if (isNaN(target.getTime())) return true
  switch (operator) {
    case 'gt': return cellDate > target
    case 'gte': return cellDate >= target
    case 'lt': return cellDate < target
    case 'lte': return cellDate <= target
    default: return true
  }
}

const resolveStickyTopValues = (stickyTopOffset, toolbarDisplay, searchAndPaginationVisible, toolbarHeight = 100) => {
  const hasCustomStickyTopOffset = Number.isFinite(stickyTopOffset) && stickyTopOffset > 0
  const stickyTopValue = hasCustomStickyTopOffset ? stickyTopOffset : inheritedStickyTopOffsetCssVar
  const hasTableToolbar = !(toolbarDisplay === 'none' || searchAndPaginationVisible === 'none')

  const stickyHeaderTopValue = hasTableToolbar
    ? (hasCustomStickyTopOffset ? stickyTopOffset + toolbarHeight : `calc(${inheritedStickyTopOffsetCssVar} + ${toolbarHeight}px)`)
    : stickyTopValue

  return { stickyTopValue, stickyHeaderTopValue }
}

/**
* @typedef {import('./utilities/objectStructures/smartMaterialUITable/smartMaterialUITable').SmartMaterialUITable} SmartMaterialUITable
* @typedef {import('./utilities/objectStructures/smartMaterialUITable/actions').Actions} Actions Actions object
* @typedef {import('./utilities/objectStructures/smartMaterialUITable/queueOrder').QueueOrder} QueueOrder QueueOrder object
*/

/**
 * @param {SmartMaterialUITable} props SmartMaterialUITable object
 */
const SmartMateriaUITable = (props) => {
  const {
    tableTuneUniqueKey = '',
    columns,
    // What an empty result says. Screens whose emptiness means something ('no BOM matches the filters')
    // pass their own; everybody else keeps the generic line.
    noDataMessage = 'No data to display',
    title,
    dataFetch,
    actions,
    onRowClick,
    rowToggle = false,
    sortOptions,
    dense,
    multipleSelection,
    searchAndPaginationVisible,
    searchDisplay,
    toolbarDisplay,
    toolbar,
    multipleSelectionActions,
    deleteMultipleSelectionDisabled,
    fetchInterval = 1,
    editingRow,
    resetMultipleSelectionValue,
    forceLoad,
    pageable = false,
    setDisableRowActions,
    onAddElementClick,
    tablePageSize = 10,
    primaryKeys = primaryKeysDefault,
    pagination,
    importFile,
    addModeByDefault,
    validator,
    multipleSelectionChange,
    handleRowCreatedInOtherSmartTable,
    rowCreatedInOtherSmartTable,
    setRowCreatedInOtherSmartTable,
    multipleOptions,
    disableSave,
    forceDisableSaveButton,
    onEditIndexChanged,
    resetPage,
    resetSort,
    setMultiEdit,
    filters,
    onFilterToggleClick,
    extraFilterParameter = extraFilterParameterDefault,
    sticky = true,
    resetSelectedRow,
    importFileDisabled,
    accordionVisibility = null,
    forceValuesChange,
    rowBackgroundColorFunction,
    toolBarStyle,
    safeDeleteShowProperty = null,
    queueOrder = null, // Used to set a new column to be sorted. {title: 'title', field: 'column', direction: 1}
    onQueueOrderChange, // Function to be called when the queue order is changed
    hideQueueButton = false,
    tableContainerStyle = {},
    stickyTopOffset = 0,
    toolbarHeight = undefined,
    // Opt-in (story 208, D8, DD8-1): confirming a row evaluates every column `condition` again on the row as it
    // is, so a row that arrives failing one is not confirmed untouched. Off by default: the other tables
    // confirm exactly as before.
    validateConditionsOnConfirm = false,
    compactHeader = false,
    searchPosition = 'right',
    extraFilterParameterMessage = null,
    customAddRecordClick = false,
    headerGroups = [],
    hideExport = false,
    // A host that keeps this toolbar hidden can still offer TUNE from its own bar: it owns the open
    // flag and is told when the dialog closes (accept, cancel or close).
    tuneOpen = false,
    onTuneClose = () => {},
    pollWhile = undefined,
    // Historia 170 (CU8, RN34b, G1-10) — "abrir con registro seleccionado": additive, optional, inert
    // when absent (S106). `preselectedRecord` = `{ field, value, searchText }` (`field`/`value`
    // identify the row — normally the primary key; `searchText` is only used for `pageable` tables,
    // where it seeds the table's OWN general search so the request already asks the server for it,
    // exactly as if the user had typed it). `onPreselectionResolved(row | null)` fires once, whether
    // the row was found or not.
    preselectedRecord = null,
    onPreselectionResolved = () => {},
    // Story 213 (C15.6, S302) — additive, inert when absent: `rowPlaceholder(row)` returns what to show in place of a whole
    // row (the blurred placeholder of a protected row while the vigilance covers) or `false`. Such a row mounts none of its
    // cells, keeps its place in the order and in the pagination, and cannot be opened nor selected.
    rowPlaceholder = () => false,
    // Story 213 (S302, DK-2) — additive, `false` by default: the list says the vigilance is covering, so "select all the data"
    // is off even when no covered row is on this page (a paged table does not see the other pages).
    disableSelectAllData = false,
    // Historia 170 (documentRepository refactor) — additive, inert when absent (default matches the
    // literal `SmartMaterialUITablePagination.jsx` already hardcoded, so every existing caller keeps
    // the exact same page-size choices unless it opts in): the rows-per-page choices offered by the
    // pageable table's own pagination control.
    rowsPerPageOptions = [3, 5, 10, 50, 75, 100],
    // Historia 170 — additive, `false` by default (inert): today, clicking a sortable column header
    // in a `pageable` table changes `sortColumnFieldPageable` but never resets `pageableObject.number`
    // back to 0 — several existing screens combine `pageable` sorting without ever needing that reset,
    // so changing the default here would be a real behaviour change for them (not just "newly useful
    // no-op"). Opt-in per table instead.
    sortResetsPageableToZero = false,
    // Historia 170 — additive, mirrors `resetPage` (which only resets the NON-pageable `page` state,
    // a no-op for `pageable` tables even though at least 4 existing screens already combine
    // `resetPage`+`pageable` — extending `resetPage` itself would change THEIR behaviour, since a
    // token change would newly trigger a real refetch/page reset where today it silently does
    // nothing). A separate, dedicated token: bumping it resets `pageableObject.number` to 0 (only
    // meaningful when `pageable`), inert otherwise.
    resetPageable,
    // Historia 170 (S94/S95) — additive: seeds the pageable general search box with a caller-supplied
    // value on mount (e.g. restored from the URL), same idea as `preselectedRecord.searchText` but
    // usable without a row to preselect. Ignored if `preselectedRecord` is present (that mechanism
    // already seeds `filterText` for its own reasons and takes precedence, unchanged from before).
    initialFilterText = '',
    // Historia 170 (S94/S95) — additive: seeds the pageable table's initial SERVER page (e.g.
    // restored from the URL) on mount, same one-shot idea as `initialFilterText`/`tablePageSize`
    // (which already seeds the initial `.size`, so no new prop was needed for that half). `0` matches
    // the previous hardcoded literal exactly, so every existing caller is unaffected.
    initialPage = 0,
    // Historia 170 (documentRepository refactor, S94/S95) — additive: seeds the per-column filter
    // refs (`columnFilters`/`columnFilterOperators` below) on mount, same one-shot idea as
    // `initialFilterText`. `{}` matches the previous hardcoded literal exactly (no caller passes
    // these today), so every existing caller is unaffected.
    initialColumnFilters = {},
    initialColumnFilterOperators = {}
  } = props
  const resolvedToolbarHeight = toolbarHeight ?? (compactHeader ? 48 : 100)
  const { stickyTopValue, stickyHeaderTopValue } = resolveStickyTopValues(stickyTopOffset, toolbarDisplay, searchAndPaginationVisible, resolvedToolbarHeight)
  const [customColumns, setCustomColumns] = useState(columns)
  // Always-latest columns, so the saved-tune loader can read them without depending on the columns
  // reference (which would re-fetch and clobber in-progress tune edits every time the parent passes
  // a new columns array).
  const columnsRef = useRef(columns)
  columnsRef.current = columns
  // Whether the saved tune has already been loaded for the current key (load once, not per render).
  const tuneLoadedRef = useRef(false)
  useEffect(() => {
    if (tableTuneUniqueKey === '') {
      setCustomColumns(columns)
    }
  }, [columns, tableTuneUniqueKey])
  const accordionVisibilityInternal = useToggleSmartAccordionVisibility()
  // S98 (G1-10) — a `pageable` table's general search is seeded with the preselection's `searchText`
  // so the FIRST request already carries it as `genericSearch` (below), same as if the user had typed
  // it. Non-paginated tables never touch this (S97: "el buscador general queda vacío") — the row is
  // located directly among the in-memory data instead.
  const [filterText, setFilterText] = useState(() => {
    if (pageable && preselectedRecord) return preselectedRecord.searchText
    if (pageable && initialFilterText) return initialFilterText
    return ''
  })
  const columnFilters = useRef(initialColumnFilters)
  // Per-column filter operators (date columns), kept parallel to columnFilters by field key.
  const columnFilterOperators = useRef(initialColumnFilterOperators)
  const [data, setData] = useState([])
  const [pageableObject, setPageableObject] = useState({ number: initialPage, size: tablePageSize })
  const [editIndex, setEditIndex] = useState(-2)
  const [selectedRow, setSelectedRow] = useState(-1)
  // Tracks the hovered record index to highlight a data row and its secondaryLine row together (opt-in via secondaryLine columns).
  const [hoveredRecordIndex, setHoveredRecordIndex] = useState(-1)
  const [selectedItem, setSelectedItem] = useState(null)
  const [columnSelectOptions, setColumnSelectOptions] = useState([])
  const [columnSelectWithSearchOptions, setColumnSelectWithSearchOptions] = useState({})
  const [page, setPage] = useState(0) // page initialize
  const [rowsPerPage, setRowsPerPage] = useState((tableTuneUniqueKey !== '') ? '' : tablePageSize) // number of rows initialize
  const [sortColumnField, setSortColumnField] = useState('')
  const [sortDirection, setSortDirection] = useState(1)
  const [sortColumnFieldPageable, setSortColumnFieldPageable] = useState('')
  const [notSavedEditingRow, setNotSavedEditingRow] = useState({})
  const [AlertElement, showAlert, dismissAlert] = AlertUI()
  const [multipleSelectionValue, setMultipleSelectionValue] = useState({
    rows: [],
    allSelected: false
  })
  const [loadTable, setLoadTable] = useState(false)
  // Story 204 — RN1.9: the text typed over a column with `filterByLabel`, kept apart from `columnFilters` (which holds
  // the codes sent to Sun). State rather than ref so the row of filters and a non-pageable table repaint on each keystroke.
  const [labelFilterTexts, setLabelFilterTexts] = useState({})
  const [loadTableColumn, setLoadTableColumn] = useState(false)
  const [
    switchBtwDataPageAndAllDataCheckbox,
    setSwitchBtwDataPageAndAllDataCheckbox
  ] = useState(false)
  const multipleSelectionChangeRef = useRef(multipleSelectionChange)
  const [isAnimating, setIsAnimating] = useState(null)

  useEffect(() => {
    multipleSelectionChangeRef.current = multipleSelectionChange
  }, [multipleSelectionChange])

  const [, setReloadAutocompleteOptions] = useState(false)

  const [firstLoad, setFirstLoad] = useState()
  const [dataForMultipleEdit, setDataForMultipleEdit] = useState([])

  const [messageToolbarMultipleSelection, setMessageToolbarMultipleSelection] =
    useState('')
  const [totalElements, setTotalElements] = useState(0)
  const requestPagination = usePagination(filters, pagination, pageableObject, columns, primaryKeys, sortColumnFieldPageable, sortOptions, filterText, columnFilters.current, queueOrder, columnFilterOperators.current)
  const [firstLoadRequestPagination, setFirstLoadRequestPagination] = useState(true)
  const requestPaginationMultiSelection = usePaginationMultiSelection(requestPagination, multipleSelectionValue, extraFilterParameter)
  const [openCreateDialog, setOpenCreateDialog] = useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [currentSelectedRow, setCurrentSelectedRow] = useState({})
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const requestPaginationRef = useRef(requestPagination)
  const [allFetchedItemsSet, setAllFetchedItemsSet] = useState([])
  const [deleteResponse, setDeleteReponse] = useState(null)
  const { format, formatTime, formatDate, formatDateTime, parseDateTime, parseDate } = useDateFormatter()
  const [openTuneTable, setOpenTuneTable] = useState(false)
  const [tableTune, setTableTune] = useState([])
  const [tableTuneFields, setTableTuneFields] = useState([])
  const [rowsPerPagePreferenceLoaded, setRowsPerPagePreferenceLoaded] = useState(tableTuneUniqueKey === '')
  // TODO This will break tune button on accordion smartTable. To fix, the workaround should be defining column param on the top componen
  // const [tableTuneLoaded, setTableTuneLoaded] = useState(false)

  const applyRowsPerPagePreference = useCallback((value) => {
    setRowsPerPage(value)
    setPage(0)
    setPageableObject(prev => ({
      ...prev,
      size: value,
      page: 0,
      number: 0
    }))
  }, [])

  const resolveRowsPerPageValue = useCallback((responseRowsPerPage) => {
    if (responseRowsPerPage && Number.isInteger(responseRowsPerPage) && responseRowsPerPage > 0) {
      return responseRowsPerPage
    }
    return tablePageSize
  }, [tablePageSize])

  const handleRowsPerPagePreferenceChanged = useCallback((value) => {
    if (tableTuneUniqueKey === '') {
      return
    }
    const saveRowsPerPage = async () => {
      await apiPut('userTableRowsPerPage/' + tableTuneUniqueKey, value)
    }
    saveRowsPerPage()
  }, [tableTuneUniqueKey])

  useEffect(() => {
    requestPaginationRef.current = requestPagination
  }, [requestPagination])

  useEffect(() => {
    setSelectedRow(-1)
  }, [resetSelectedRow])

  useEffect(() => {
    setPage(0)
  }, [resetPage])

  // Historia 170 — additive, dedicated pageable counterpart of `resetPage` above (see its prop
  // comment for why `resetPage` itself is not reused for this). Runs only on a real token CHANGE
  // (never on mount), same convention as `resetPage`'s own effect.
  const resetPageableMounted = useRef(false)
  useEffect(() => {
    if (!resetPageableMounted.current) {
      resetPageableMounted.current = true
      return
    }
    if (!pageable) return
    setPageableObject((previous) => ({ ...previous, number: 0 }))
    setLoadTable((previous) => !previous)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetPageable])

  // Opt-in `resetSort` (a changing token, like `resetPage`): every change after the first render drops the
  // user's sort, and the default-sort effect below re-applies the table's default (queue order or
  // `sortOptions.default`). Tables that never pass it are unaffected.
  const resetSortInitializedRef = useRef(false)
  useEffect(() => {
    if (!resetSortInitializedRef.current) {
      resetSortInitializedRef.current = true
      return
    }
    setSortColumnFieldPageable('')
    setSortColumnField('')
    setSortDirection(1)
  }, [resetSort])

  useEffect(() => {
    if (tableTuneUniqueKey === '') {
      setRowsPerPagePreferenceLoaded(true)
      return
    }
    const getRowsPerPage = async () => {
      setRowsPerPagePreferenceLoaded(false)
      try {
        const responseRowsPerPage = await apiGet('userTableRowsPerPage/' + tableTuneUniqueKey)
        applyRowsPerPagePreference(resolveRowsPerPageValue(responseRowsPerPage))
      } catch (exception) {
        applyRowsPerPagePreference(tablePageSize)
      } finally {
        setRowsPerPagePreferenceLoaded(true)
      }
    }
    getRowsPerPage()
  }, [tableTuneUniqueKey, applyRowsPerPagePreference, resolveRowsPerPageValue, tablePageSize])

  // Load the saved tune ONCE per key. Depending on `columns` here (as before) re-fetched the tune
  // and reset the tune fields to the server value on every new columns array the parent passed —
  // e.g. when an async cell dependency (main image resolution) makes the parent re-render — which
  // discarded the user's in-progress tune changes ("reverts to the value from before the change").
  useEffect(() => {
    tuneLoadedRef.current = false
    const getTune = async () => {
      try {
        const response = await apiGet('user/tabletune/' + tableTuneUniqueKey)
        if (response.fields) {
          const merged = mergeColumnsAndTunedFields(columnsRef.current, response.fields)
          updateTableTuneFields(merged)
          setTableTune(response)
          setCustomColumns(merged)
        } else {
          const merged = mergeColumnsAndTunedFields(columnsRef.current, [])
          const fields = updateTableTuneFields(merged)
          setTableTune({ id: null, tableKey: tableTuneUniqueKey, fields })
          setCustomColumns(merged)
        }
      } catch (error) {
        const merged = mergeColumnsAndTunedFields(columnsRef.current, [])
        const fields = updateTableTuneFields(merged)
        setTableTune({ id: null, tableKey: tableTuneUniqueKey, fields })
        setCustomColumns(merged)
      }
      tuneLoadedRef.current = true
    }
    if (tableTuneUniqueKey !== '') {
      getTune()
    }
  }, [tableTuneUniqueKey])

  // When the columns prop changes after the tune is loaded, keep the column definitions (render
  // closures, widths, etc.) fresh WITHOUT re-fetching the saved tune or touching the in-progress
  // tune fields: re-apply the currently applied show/hide/order onto the fresh column defs.
  useEffect(() => {
    if (tableTuneUniqueKey === '' || !tuneLoadedRef.current) {
      return
    }
    setCustomColumns((previous) => {
      const currentFields = previous.map((column) => ({ name: column.field, show: !column.hide, order: column.order }))
      return mergeColumnsAndTunedFields(columns, currentFields)
    })
  }, [columns, tableTuneUniqueKey])

  const columnsForRender = useMemo(() => {
    const isAddMode = editIndex === -1

    const calculatedColumns = customColumns.filter((customColumn) => {
      const column = columns.find((item) => item.field === customColumn.field)
      if (column === undefined) return false
      // secondaryLine columns are rendered as a full-width row below each record, not as a cell.
      if (column.secondaryLine) return false
      if (column.hide) {
        return false
      }
      if (isAddMode) {
        if (column.showWhenColumnVisible) {
          // We dont have the info yet. Will be checked later
          return true
        }
        if (typeof column.mandatory === 'function' || column.mandatory) {
          return true
        }
      }
      if (customColumn.hide) {
        return false
      }
      return true
    })

    if (!isAddMode) {
      return calculatedColumns
    }

    const finalColumns = calculatedColumns.filter((customColumn) => {
      const column = columns.find((item) => item.field === customColumn.field)
      if (!column.showWhenColumnVisible) {
        return true
      }
      if (Array.isArray(column.showWhenColumnVisible)) {
        return column.showWhenColumnVisible.filter((showColumn) => calculatedColumns.find((item) => item.field === showColumn)).length > 0
      } else {
        const item = calculatedColumns.find((item) => item.field === column.showWhenColumnVisible)
        return item !== undefined
      }
    })

    return finalColumns.map((item) => { return { ...item, hide: false } })
  }, [customColumns, columns, editIndex])

  // Columns flagged as `secondaryLine` are rendered as a full-width line below each record
  // (not as a cell). They still live in customColumns, so the tune dialog can show/hide them.
  const secondaryLineColumns = useMemo(() => {
    return customColumns.filter((customColumn) => {
      const column = columns.find((item) => item.field === customColumn.field)
      return Boolean(column && column.secondaryLine && !column.hide && !customColumn.hide)
    })
  }, [customColumns, columns])
  const hasSecondaryLine = secondaryLineColumns.length > 0

  const mergeColumnsAndTunedFields = (columns, fields) => {
    const mergedColumns = columns.map((column, index) => {
      const customField = fields.find(item => item.name === column.field)
      if (customField) {
        return { ...column, hide: !customField.show, order: customField.order && customField.show ? customField.order : Infinity }
      } else {
        return { ...column, hide: false, order: index + 1 }
      }
    })
    return mergedColumns.sort((a, b) => a.order - b.order)
  }

  const saveTableTuneFields = () => {
    const postFields = async (data) => {
      const response = await apiPost('user/tabletune', data)
      if (response.fields) {
        const merged = mergeColumnsAndTunedFields(columns, tableTuneFields)
        setTableTune(response)
        setCustomColumns(merged)
      }
    }
    // `label` is only ever painted inside the TABLE TUNE dialog itself (`TableTune.jsx:135`); Sun's
    // `UserTableTuneFieldDto` doesn't even have such a field. Stripped here so a custom-language
    // redefinition of a column's header never travels in a request to Sun (03-dominio.md §138/§139).
    const data = { ...tableTune, fields: tableTuneFields.map(({ label, ...rest }) => rest) }
    postFields(data)
  }

  const updateTableTuneFields = (columns) => {
    const fields = columns.map((column, index) => (
      {
        label: column.tuneLabel ? column.tuneLabel : column.name,
        name: column.field,
        show: !column.hide,
        order: column.order
      }))
    setTableTuneFields(fields)
    return fields
  }

  const getAccordionVisibility = () => {
    if (accordionVisibility !== null) {
      return accordionVisibility
    }
    return accordionVisibilityInternal
  }
  const handleColumnsErrors = useCallback(() => {
    const arrayColumns = []
    columns.forEach((column) => {
      arrayColumns[arrayColumns.length] = {
        field: column.field,
        error: false,
        errorLabel: '',
        label: ''
      }
    })
    return arrayColumns
  }, [columns])

  const handleSelectValue = (row, column) => {
    const optionsData = column.edit?.sourceWithSearch ? columnSelectWithSearchOptions[column.name] : columnSelectOptions[column.name]
    return (optionsData &&
      getObjectPropertyFromString(row, column.field) !== undefined
      ? getObjectPropertyFromString(row, column.field)
      : '')
  }

  const [errorTextfield, setErrorTextfield] = useState(handleColumnsErrors)
  const columnFilterChanged = useRef([])

  // The warnings belong to the row being edited. Hosts that build their columns inline hand a new array
  // on every render, and that must not wipe them while the user types (story 208, D8): only a different
  // set of fields starts again from a clean slate.
  const columnFieldsKey = columns.map((column) => column.field).join('|')
  useEffect(() => {
    setErrorTextfield(handleColumnsErrors())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFieldsKey])

  const createNewElement = async () => {
    const newElement = {}
    for (const column of columns) {
      const sp = column.field.split('.')
      let def = ''
      if (sp.length > 1) {
        def = null
      }
      if (column.defaultNewValue !== undefined) {
        if (typeof column.defaultNewValue === 'function') {
          newElement[sp[0]] = await column.defaultNewValue()
        } else {
          newElement[sp[0]] = column.defaultNewValue
        }
      } else {
        newElement[sp[0]] = def
      }
    }
    newElement[smartTableIndexKey] = -1
    return newElement
  }

  const getIntervalTableData = async () => {
    let dbData = []
    if (tableTuneUniqueKey !== '' && !rowsPerPagePreferenceLoaded && !setMultiEdit) {
      return []
    }
    if (requestPagination.size === undefined || requestPagination.size === null) {
      return []
    }
    if (pageable && Object.keys(requestPagination).length === 0) {
      return []
    }
    if (typeof dataFetch === 'function') {
      dbData = await dataFetch(requestPagination)
    } else if (typeof dataFetch === 'string') {
      dbData = await apiGet(dataFetch, pageable ? requestPagination : null)
    } else if (Array.isArray(dataFetch)) {
      dbData = [...dataFetch]
    }
    if (dbData === undefined) {
      setFirstLoad(true)
      return []
    }
    if (safeDeleteShowProperty !== null) {
      setAllFetchedItemsSet(prevItems => arrayUnionNoDuplicates(prevItems, pageable ? dbData.content : dbData, 'id'))
    }
    if (pageable) {
      const copyContent = { ...dbData }
      if (copyContent.content !== undefined) {
        dbData = copyContent.content
      }
      delete copyContent.content
      // Check if the request is not stale (page/size changed while request was in flight)
      if (requestPagination.page === requestPaginationRef.current.page &&
          requestPagination.size === requestPaginationRef.current.size) {
        setPageableObject(copyContent)
      }
    }

    if (multipleSelectionValue.allSelected) {
      if (totalElements !== pageableObject.totalElements) {
        multipleSelectionValue.rows = []
      }
    }

    setTotalElements(pageableObject.totalElements)
    setFirstLoad(true)
    if (!firstLoad) {
      setDataForMultipleEdit(dbData)
    }
    return dbData.map((row, index) => {
      return { ...row, [smartTableIndexKey]: index }
    })
  }

  const addModeByDefaultSetDone = useRef(false)

  const updateSmartInterval = (intervalData) => {
    if (pageable && (
      requestPagination.page !== requestPaginationRef.current.page ||
      requestPagination.size !== requestPaginationRef.current.size
    )) {
      return
    }

    // Ignore updates if we are adding a new row (SmartInterval is cancelled but it can call the method if it was allready running)
    if (editIndex !== -1) {
      if (!firstLoad) {
        setData((prev) => {
          const addElement = prev.find(item => item[smartTableIndexKey] === -1)
          if (addElement === undefined) {
            return intervalData
          }
          return [addElement, ...intervalData]
        })
      } else {
        setData(intervalData)
      }

      // On first load set in create new element mode if required
      if (!addModeByDefaultSetDone.current) {
        if (addModeByDefault) {
          onAddNewElementClick()
        }
        addModeByDefaultSetDone.current = true
      }

      // pollWhile (opt-in): stop the interval after a fetch if the predicate returns false.
      // Only evaluated when the table is in read mode (editIndex === -2) and we have received
      // actual data (intervalData.length > 0) to avoid stopping the interval on precondition
      // guard returns (empty array before requestPagination is ready).
      if (typeof pollWhile === 'function' && editIndex === -2 && intervalData.length > 0) {
        if (!pollWhile(intervalData)) {
          if (typeof startTableIntervalRef.current === 'function') {
            startTableIntervalRef.current(false)
          }
        }
      }
    }
  }

  const checkSelectedItemInData = (indexedData) => {
    getTableDataFiltered(indexedData).every((item, index) => {
      if (checkIsSelectedItem(item)) {
        const newPage = Math.floor(index / rowsPerPage)
        const selectedRowIndex = index % rowsPerPage
        setPage(newPage)
        setSelectedRow(selectedRowIndex)
        return false
      }
      return true
    })
  }

  const checkIsSelectedItem = (item) => {
    let found = true
    columns.every(column => {
      const selectedItemValue = getObjectPropertyFromString(selectedItem, column.field)
      const itemValue = getObjectPropertyFromString(item, column.field)
      if (selectedItemValue !== itemValue) {
        found = false
        return false
      }
      return true
    })

    return found
  }
  const reduceRowPropertiesToDesired = (keys, row) => {
    const copyRowReduced = {}
    keys.forEach(name => {
      if (row[name] !== undefined) { copyRowReduced[name] = row[name].toString() }
    })
    return copyRowReduced
  }

  const handleOnChange = (row) => {
    let copyMultipleSelectionValue = multipleSelectionValue
    const copyRow = reduceRowPropertiesToDesired(primaryKeys, row)

    const findRowOnMultipleSelectionValue = copyMultipleSelectionValue.rows.find(row => JSON.stringify(row) === JSON.stringify(copyRow))

    if (findRowOnMultipleSelectionValue === undefined) {
      copyMultipleSelectionValue = { ...copyMultipleSelectionValue, rows: [...copyMultipleSelectionValue.rows, copyRow] }
    } else {
      copyMultipleSelectionValue = { ...copyMultipleSelectionValue, rows: copyMultipleSelectionValue.rows.filter(row => JSON.stringify(row) !== JSON.stringify(copyRow)) }
    }

    setNewMultipleSelectionValue(copyMultipleSelectionValue)
  }

  const setNewMultipleSelectionValue = (multipleSelectionValue) => {
    setMultipleSelectionValue(multipleSelectionValue)
    if (multipleSelectionChangeRef.current !== undefined && multipleSelectionChangeRef.current !== null && typeof multipleSelectionChangeRef.current === 'function') { multipleSelectionChangeRef.current(multipleSelectionValue) }
  }

  const resetMultiSelectionValue = () => {
    const multiSelection = {
      rows: [],
      allSelected: false
    }
    setMultipleSelectionValue(multiSelection)
    if (multipleSelectionChangeRef.current !== undefined && multipleSelectionChangeRef.current !== null && typeof multipleSelectionChangeRef.current === 'function') { multipleSelectionChangeRef.current(multiSelection) }
  }

  const startTableInterval = useSmartInterval(getIntervalTableData, fetchInterval * 1000, updateSmartInterval)

  const onAddNewElementClick = async () => {
    if (customAddRecordClick) {
      if (onAddElementClick && typeof onAddElementClick === 'function') {
        onAddElementClick()
      }
      return
    }
    setEditIndex(-1)

    const newElement = await createNewElement()

    if (onEditIndexChanged && typeof onEditIndexChanged === 'function') {
      onEditIndexChanged(-1, newElement)
    }
    if (onAddElementClick && typeof onAddElementClick === 'function') onAddElementClick(newElement)
    setData(prevData => [newElement, ...prevData])
    startTableInterval(false)
  }

  const getIntervalColumnSelectOptions = async () => {
    const options = []
    const errors = []
    for (const column of columns) {
      if (
        column.edit &&
        getColumnType(column, null) &&
        (getColumnType(column, null) === 'select' || getColumnType(column, null) === 'select-plain' || getColumnType(column, null) === 'smartMultiSelect' || getColumnType(column, null) === 'smartSelect')) {
        if (typeof column.edit.source === 'function') {
          const dataOptions = await column.edit.source().catch((error) => {
            errors.push(error)
          })
          options[column.name] = {
            id: column.edit.id,
            field: column.edit.field,
            data: dataOptions
          }
        } else if (typeof column.edit.source === 'object') {
          options[column.name] = column.edit.source
        }
      }
    }
    return { options, errors }
  }

  const setIntervalColumnSelectOptionsData = (columnData) => {
    if (columnData.errors.length > 0) {
      showAlert({ message: columnData.errors[0].message, severity: columnData.errors[0].name })
    }

    setColumnSelectOptions(columnData.options)
  }

  const startColumnSelectOptionsInterval = useSmartInterval(getIntervalColumnSelectOptions, fetchInterval * 1000, setIntervalColumnSelectOptionsData, false)

  const startTableIntervalRef = useRef()
  const startColumnTableIntervalRef = useRef()

  useEffect(() => {
    const getColumnsData = (editIndex > -2)
    if (typeof startTableIntervalRef.current === 'function') {
      startTableIntervalRef.current(!getColumnsData)
    }
    if (typeof startColumnTableIntervalRef.current === 'function') {
      startColumnTableIntervalRef.current(getColumnsData, true)
    }
  }, [editIndex])

  useEffect(() => {
    startTableIntervalRef.current = startTableInterval
  }, [startTableInterval])

  useEffect(() => {
    startColumnTableIntervalRef.current = startColumnSelectOptionsInterval
  }, [startColumnSelectOptionsInterval])

  useEffect(() => {
    startTableIntervalRef.current(true, true)
  }, [forceLoad, loadTable])

  useEffect(() => {
    startColumnTableIntervalRef.current(false, true)
  }, [forceLoad, loadTable, loadTableColumn])

  useEffect(() => {
    if (setMultiEdit !== undefined) {
      startTableInterval(false)
    }

    return () => {

    }
  }, [setMultiEdit, startTableInterval])
  useEffect(() => {
    if (!pageable) {
      if (sortColumnField === '') {
        if (queueOrder && queueOrder.field && queueOrder.direction) {
          setSortColumnField(queueOrder.field)
          setSortDirection(queueOrder.direction)
        } else if (sortOptions?.default?.field) {
          setSortColumnField(sortOptions.default.field)
          if (sortOptions.default?.direction === -1) {
            setSortDirection(-1)
          }
        } else if (columns?.length > 0 && (!sortOptions || !sortOptions.disableAutoSort)) {
          setSortColumnField(columns[0].field)
        }
      }
    } else {
      if (sortColumnFieldPageable === '') {
        if (queueOrder && queueOrder.field && queueOrder.direction) {
          setSortColumnFieldPageable({
            field: queueOrder.field,
            direction: queueOrder.direction
          })
        } else if (sortOptions?.default?.field) {
          if (!sortColumnFieldPageable.direction) {
            setSortColumnFieldPageable({
              field: sortOptions.default.field,
              direction: sortOptions.default.direction
            })
          }
        }
      }
    }
  }, [columns, sortColumnField, sortOptions, pageable, setSortColumnFieldPageable, sortColumnFieldPageable, queueOrder])

  useEffect(() => {
    if (Object.keys(requestPagination).length !== 0 && firstLoadRequestPagination) {
      startTableIntervalRef.current(true, true)
      setFirstLoadRequestPagination(false)
    }
  }, [requestPagination, firstLoadRequestPagination])

  const totalElementsMainTable = useRef(0)

  useEffect(() => {
    const fetch = async () => {
      let selectableRecords, selectedQuantity
      if (typeof dataFetch === 'function') {
        selectableRecords = await dataFetch(requestPaginationMultiSelection)
      } else if (typeof dataFetch === 'string') {
        selectableRecords = await apiGet(dataFetch, pageable ? requestPaginationMultiSelection : null)
      }

      if (pageable) {
        selectedQuantity = selectableRecords.totalElements
      } else {
        selectedQuantity = requestPaginationMultiSelection.rows.length
      }

      setMessageToolbarMultipleSelection(selectedQuantity + ' ' + t_('items selected'))
    }
    if (Object.keys(requestPaginationMultiSelection).length !== 0) {
      fetch()
    }
  }, [requestPaginationMultiSelection, dataFetch, pageable, data.length, pageableObject.totalElements, t_])

  useEffect(() => {
    totalElementsMainTable.current = data.length
  }, [data.length])
  useEffect(() => {
    totalElementsMainTable.current = pageableObject.totalElements
  }, [pageableObject.totalElements])

  const getObjectPropertyFromString = useCallback((obj, str, defaultValue) => {
    str = str.replace(/\[(\w+)\]/g, '.$1') // convert indexes to properties
    str = str.replace(/^\./, '') // strip a leading dot
    const a = str.split('.')
    for (let i = 0, n = a.length; i < n; ++i) {
      const k = a[i]
      if (obj !== null && obj !== undefined && obj !== 'undefined' && k in obj && obj[k] !== null) {
        obj = obj[k]
      } else {
        return defaultValue
      }
    }

    if (obj == null || obj === undefined || obj === '') {
      return ''
    }
    if (queueOrder && queueOrder.field === str) {
      return obj
    }
    const column = columns.find(col => col.field === str)

    return getColumnType(column, null) === 'datetime' || getColumnType(column, null) === 'dateTimePicker'
      ? formatDateTime(obj)
      : getColumnType(column, null) === 'date' || getColumnType(column, null) === 'datePicker'
        ? formatDate(obj)
        : getColumnType(column, null) === 'time' ? formatTime(obj) : obj
  }, [columns, queueOrder, formatDateTime, formatDate, formatTime])

  const getDateTimeFromString = (obj, str, defaultValue) => {
    return parseDateTime(getObjectPropertyFromString(obj, str, defaultValue))
  }

  const getDateFromString = (obj, str, defaultValue) => {
    return parseDate(getObjectPropertyFromString(obj, str, defaultValue))
  }

  const dataAvailable = useRef(false)

  const getVisualizedData = (rawData) => {
    const rData = rawData || (setMultiEdit !== undefined ? dataForMultipleEdit : data)
    const visualizedData = []
    let offset = 0
    if (editIndex === -1 && !pageable) {
      offset = 1
      const editingRow = getEditingRow()
      if (editingRow) {
        visualizedData.push(editingRow)
      }
    }

    let value = [...visualizedData, ...rData]
    if (!pageable && setMultiEdit === undefined) {
      //   return [...visualizedData, ...rData]
      // } else {
      value = [
        ...visualizedData,
        ...getTableDataFiltered(rData).slice(
          page * rowsPerPage,
          page * rowsPerPage + rowsPerPage - offset
        )
      ]
    } else if (!pageable) {
      value = [
        ...visualizedData,
        ...getTableDataFiltered(rData)
      ]
    }
    if (selectedItem) {
      checkSelectedItemInData(value)
      setSelectedItem(null)
    }

    if (value.length > 0) {
      dataAvailable.current = true
    } else {
      dataAvailable.current = false
    }

    return value
  }
  const getEditingRow = useCallback(() => {
    return data.find((row) => rowHasTableIndexDefined(row) && (row[smartTableIndexKey] === editIndex || setMultiEdit))
  }, [data, editIndex, setMultiEdit])

  const validateMultiSelectColumn = (row, column) => {
    let result

    if (getColumnType(column, row) === 'smartMultiSelect' && multipleOptions) {
      const editingRowProperty = getEditingRowProperty(column.field)
      const value = row[editingRowProperty.editingRowProperty]
      if (value != null && value.length > 0) {
        result = false
      } else {
        result = true
      }
    } else {
      result = false
    }
    return result
  }

  const validateSingleAutocompleteSelectColumn = (row, column) => {
    let result
    if (getColumnType(column, row) === 'smartMultiSelect' && !multipleOptions) {
      const editingRowProperty = getEditingRowProperty(column.field)
      const value = row[editingRowProperty.editingRowProperty]
      if (value != null && Object.keys(value).length > 0) {
        result = false
      } else {
        result = true
      }
    }

    return result
  }

  const resetPageCount = async () => {
    if (page !== 0 || (pageable && pageableObject.number !== 0)) {
      setPage(0)
    }
  }

  useEffect(() => {
    resetMultiSelectionValue()
  }, [resetMultipleSelectionValue])

  const validateColumError = (row, columns) => {
    for (let i = 0; i < columns.length; i++) {
      if (
        mandatory(columns[i], row, i) &&
        (((getObjectPropertyFromString(row, columns[i].field) === undefined ||
          getObjectPropertyFromString(row, columns[i].field) === '') && columns[i].type !== 'smartMultiSelect') ||
          (validateMultiSelectColumn(row, columns[i]) || validateSingleAutocompleteSelectColumn(row, columns[i])))) {
        setErrorValidation(errorTextfield, i, setErrorTextfield)
      } else if (Object.prototype.hasOwnProperty.call(columns[i], 'type') &&
        columns[i].type === 'dateTimePicker' && row[columns[i].field] && mandatory(columns[i], row, i)) {
        if (!isValidDateTime(getObjectPropertyFromString(row, columns[i].field))) {
          setErrorValidation(errorTextfield, i, setErrorTextfield)
        }
      } else if (Object.prototype.hasOwnProperty.call(columns[i], 'type') &&
        columns[i].type === 'datePicker' && row[columns[i].field] && mandatory(columns[i], row, i)) {
        if (!isValidDate(getObjectPropertyFromString(row, columns[i].field))) {
          setErrorValidation(errorTextfield, i, setErrorTextfield)
        }
      }
    }

    // Check if custom validator has been provided
    if (typeof validator === 'function') {
      const error = validator(row)
      if (error) {
        let errorFieldList
        if (Array.isArray(error)) {
          errorFieldList = error
        } else if (Array.isArray(error.errorFields)) {
          errorFieldList = error.errorFields
        }
        if (errorFieldList.length > 0) {
          for (let i = 0; i < errorFieldList.length; i++) {
            let j = 0
            while (j < columns.length) {
              if (columns[j].field === errorFieldList[i]) {
                setErrorValidation(errorTextfield, j, setErrorTextfield)
                break
              } else {
                j++
              }
            }
          }
        }
        if (typeof error.alertMessage === 'string') {
          showAlert(
            {
              title: error.alertMessage,
              severity: 'warning'
            }
          )
        }
      }
    }

    // Opt-in (story 208, D8, S52): a row is confirmed only while every column condition holds on it. The
    // warnings on screen may never have been raised, on a row confirmed untouched.
    if (validateConditionsOnConfirm) {
      const conditionErrors = [...errorTextfield]
      let conditionFailed = false
      for (let i = 0; i < columns.length; i++) {
        const errorMsg = condition(columns[i], row)
        if (errorMsg) {
          conditionErrors[i].error = true
          conditionErrors[i].errorLabel = errorMsg
          conditionFailed = true
        }
      }
      if (conditionFailed) {
        setErrorTextfield(conditionErrors)
        return true
      }
    }

    const error = errorTextfield.find(
      (column) => column.error === true
    )
    if (error) return true
    return false
  }

  const isValidDateTime = (value) => {
    return moment(value, format.dateTime).isValid()
  }

  const isValidDate = (value) => {
    return moment(value, format.date).isValid()
  }

  const handleLabel = (column, row, columnIndex) => {
    if (mandatory(column, row, columnIndex)) {
      return t_('Required *')
    } else {
      return ''
    }
  }

  const mandatory = (column, row, columnIndex) => {
    let mandatory = false
    if (column.mandatory && typeof column.mandatory === 'function') {
      const mandatoryFunction = (elem) => {
        mandatory = column.mandatory(elem)
      }
      mandatoryFunction(row)
    } else {
      mandatory = column.mandatory
    }
    return mandatory
  }

  const getEditingRowProperty = useCallback((property) => {
    let editingRow = getEditingRow()
    let pList = property.split('.')
    if (pList.length > 1) {
      pList = pList.slice(0, pList.length - 1)
    }

    for (let i = 0; i < pList.length - 1; i++) {
      const elem = pList[i]
      if (!editingRow[elem]) editingRow[elem] = {}
      editingRow = editingRow[elem]
    }
    return {
      editingRow,
      editingRowProperty: pList[pList.length - 1]
    }
  }, [getEditingRow])

  const condition = useCallback((column, row) => {
    let condition = ''
    if (column.condition && typeof column.condition === 'function') {
      const conditionFunction = (row, column) => {
        const columnField = getEditingRowProperty(column.field).editingRowProperty
        condition = column.condition(row, columnField)
      }
      conditionFunction(row, column)
    }
    return condition
  }, [getEditingRowProperty])

  const checkColumnsCondition = useCallback((currentRow, value) => {
    const copyErrorTextfield = [...errorTextfield]
    for (let x = 0; x < columns.length; x++) {
      if (columns[x].condition !== undefined) {
        const errorMsg = condition(columns[x], currentRow, value)
        if ((errorMsg !== '' && errorMsg !== undefined)) {
          copyErrorTextfield[x].error = true
          copyErrorTextfield[x].errorLabel = errorMsg
          setErrorTextfield(copyErrorTextfield)
        } else {
          copyErrorTextfield[x].error = false
          copyErrorTextfield[x].errorLabel = ''
          setErrorTextfield(copyErrorTextfield)
        }
      }
    }
  }, [columns, condition, errorTextfield])

  const formatNumberDecimals = (value, decimals) => {
    if (value < 0) {
      value = Math.abs(value).toString()
    }
    const decimalParts = value.split('.')
    if (decimalParts.length > 1) {
      value = decimalParts[0] + '.' + decimalParts[1].slice(0, decimals)
    }
    return value
  }

  const evaluateColumnNumeric = (column, row) => {
    if (!column) return false
    if (typeof column.numeric === 'function') {
      try {
        return !!column.numeric(row)
      } catch (e) {
        return false
      }
    }
    return !!column.numeric
  }

  const getColumnType = (column, row) => {
    if (!column) return undefined
    if (typeof column.type === 'function') {
      return column.type(row)
    }
    return column.type
  }

  const isDateFilterColumn = (column) => {
    if (!column) return false
    if (column.filterType === 'date') return true
    return isDateColumnType(getColumnType(column, null))
  }

  const changeTextValue = useCallback((value, column, columnIndex, row) => {
    const copyErrorTextfield = [...errorTextfield]
    // Clear previous errors on any input change to re-enable Save
    for (let i = 0; i < copyErrorTextfield.length; i++) {
      copyErrorTextfield[i].error = false
      copyErrorTextfield[i].errorLabel = ''
    }
    setErrorTextfield(copyErrorTextfield)

    let errorFound = false

    if (evaluateColumnNumeric(column, row) && typeof value === 'string' && column.decimals !== undefined) {
      value = formatNumberDecimals(value, column.decimals)
    }

    if (column.unique) {
      const found = data.find(
        (element) => {
          const dataValue = getObjectPropertyFromString(element, column.field)
          return dataValue !== undefined ? dataValue.toString() === value : false
        }
      )

      if (!found) {
        copyErrorTextfield[columnIndex].error = false
        copyErrorTextfield[columnIndex].errorLabel = ''
      } else {
        copyErrorTextfield[columnIndex].error = true
        copyErrorTextfield[columnIndex].errorLabel = t_('Key duplicated')
        errorFound = true
        setErrorTextfield(columnIndex)
      }
      setErrorTextfield(copyErrorTextfield)
    } else if (!errorFound && mandatory(column, row) && value !== undefined && value !== '') {
      copyErrorTextfield[columnIndex].error = false
      copyErrorTextfield[columnIndex].errorLabel = ''
      copyErrorTextfield[columnIndex].label = ''
      setErrorTextfield(copyErrorTextfield)
    }

    // Check dateTimePicker if value is valid
    if (getColumnType(column, row) === 'dateTimePicker' || getColumnType(column, row) === 'datePicker') {
      if (mandatory(column, row) && value instanceof Date && isNaN(value)) {
        copyErrorTextfield[columnIndex].error = true
        copyErrorTextfield[columnIndex].errorLabel = 'Invalid format'
        setErrorTextfield(copyErrorTextfield)
        errorFound = true
      } else {
        copyErrorTextfield[columnIndex].error = false
      }
    }

    // Check if manadatory and is empty or null
    if (!errorFound && mandatory(column, row) && (!value || (typeof value === 'string' && value.trim().length === 0))) {
      copyErrorTextfield[columnIndex].error = true
      copyErrorTextfield[columnIndex].errorLabel = t_('Required *')
      setErrorTextfield(copyErrorTextfield)
      errorFound = true
    }

    const propertySplit = column.field.split('.')

    if (setMultiEdit === undefined) {
      const editingRowProperty = getEditingRowProperty(column.field)
      let propertyParent = editingRowProperty.editingRow

      // No row is currently in edit mode (e.g. a stale forceValuesChange fired
      // after the data was refreshed): there is nothing to update, so bail out
      // instead of dereferencing an undefined row and crashing the whole view.
      if (!propertyParent) {
        return
      }

      for (let i = 0; i < propertySplit.length - 1; i++) {
        propertyParent = propertyParent[propertySplit[i]]
      }

      propertyParent[propertySplit[propertySplit.length - 1]] = value

      if (!errorFound && column.condition !== undefined) {
        checkColumnsCondition(propertyParent)
      }
      setData((previousData) =>
        previousData.map((row) => {
          if (rowHasTableIndexDefined(row) && row[smartTableIndexKey] === editIndex) {
            return editingRowProperty.editingRow
          }
          return row
        })
      )
      if (column.onChange && typeof column.onChange === 'function') {
        column.onChange(editingRowProperty.editingRow)
      }
      // Silent custom validation on change
      if (typeof validator === 'function') {
        const valError = validator(editingRowProperty.editingRow)
        const freshErrors = [...errorTextfield]
        if (valError) {
          let fields = []
          if (Array.isArray(valError)) fields = valError
          else if (Array.isArray(valError.errorFields)) fields = valError.errorFields
          if (fields.length > 0) {
            fields.forEach((field) => {
              const idx = columns.findIndex((c) => c.field === field)
              if (idx >= 0) {
                freshErrors[idx].error = true
                freshErrors[idx].errorLabel = freshErrors[idx].errorLabel || ''
              }
            })
            setErrorTextfield(freshErrors)
          }
        }
      }
    } else {
      const copyDataForMultipleEdit = [...dataForMultipleEdit]

      copyDataForMultipleEdit.map((dataItem) => {
        if (row.id === dataItem.id) {
          dataItem[column.field] = value
        }
        return dataItem
      })

      setMultiEdit(copyDataForMultipleEdit)
      setDataForMultipleEdit(copyDataForMultipleEdit)
    }

    // This line was substituted because it was not valid for nested properties
    // editingRowProperty.editingRow[editingRowProperty.editingRowProperty] = value;
  }, [checkColumnsCondition, data, dataForMultipleEdit, editIndex, errorTextfield, t_, setMultiEdit, getEditingRowProperty, getObjectPropertyFromString, columns, validator])

  // Used to change data from outside SmartTable
  useEffect(() => {
    if (forceValuesChange) {
      forceValuesChange.rowsToChange.forEach((rowToChange) => {
        const row = data.find((row) => row['smart-table-index'] === rowToChange['smart-table-index'])
        if (!row) {
          return
        }
        rowToChange.valuesToChange.forEach((valueToChange) => {
          if (row[valueToChange.field] === valueToChange.value) {
            return
          }
          const columnIndex = columns.findIndex(column => column.field === valueToChange.field)
          changeTextValue(valueToChange.value, columns[columnIndex], columnIndex, row)
        })
      })
    }
  }, [forceValuesChange, changeTextValue, columns, data])

  const changeSelectValue = (text, column, columnIndex, row) => {
    const optionsData = column.edit?.sourceWithSearch ? columnSelectWithSearchOptions[column.name] : columnSelectOptions[column.name]
    let columnValue
    if (optionsData.length > 0) {
      columnValue = text
    } else {
      columnValue = getValueFromColumnSelectOptionsData(optionsData.data, text, column.edit.field)
    }
    changeEditedSelect(column, columnValue, columnIndex, row)
    setLoadTableColumn(!loadTableColumn)
  }

  const getValueFromColumnSelectOptionsData = (data, text, columnEditField) => {
    return data.find((d) => {
      if (typeof d === 'string') {
        return d === text
      } else {
        return d[columnEditField] === text
      }
    })
  }

  const changeEditedSelect = (column, value, columnIndex, row) => {
    const copyErrorTextfield = [...errorTextfield]
    for (let i = 0; i < copyErrorTextfield.length; i++) {
      copyErrorTextfield[i].error = false
      copyErrorTextfield[i].errorLabel = ''
    }
    setErrorTextfield(copyErrorTextfield)
    const editingRowProperty = getEditingRowProperty(column.field)
    const currentRow = row
    currentRow[editingRowProperty.editingRowProperty] = value
    if (mandatory(column, row) && value !== undefined && value !== '') {
      copyErrorTextfield[columnIndex].error = false
      copyErrorTextfield[columnIndex].errorLabel = ''
      setErrorTextfield(copyErrorTextfield)
    }

    if (column.condition !== undefined) {
      checkColumnsCondition(currentRow, value)
    }

    editingRowProperty.editingRow[editingRowProperty.editingRowProperty] =
      value
    setData((previousData) =>
      previousData.map((row) => {
        if (rowHasTableIndexDefined(row) && row[smartTableIndexKey] === editIndex) {
          return editingRowProperty.editingRow
        }
        return row
      })
    )
    if (setMultiEdit) {
      // Multiple edit mode: keep existing logic
      const rootProperty = column.field.split('.')[0]
      const copyDataForMultipleEdit = [...dataForMultipleEdit]
      copyDataForMultipleEdit.map((dataItem) => {
        if (row.id === dataItem.id) {
          dataItem[rootProperty] = value
        }
        return dataItem
      })
      setMultiEdit(copyDataForMultipleEdit)
      setDataForMultipleEdit(copyDataForMultipleEdit)
    }
    if (column.onChange && typeof column.onChange === 'function') {
      column.onChange(editingRowProperty.editingRow)
    }
    if (typeof validator === 'function') {
      const valError = validator(editingRowProperty.editingRow)
      const freshErrors = [...errorTextfield]
      if (valError) {
        let fields = []
        if (Array.isArray(valError)) fields = valError
        else if (Array.isArray(valError.errorFields)) fields = valError.errorFields
        if (fields.length > 0) {
          fields.forEach((field) => {
            const idx = columns.findIndex((c) => c.field === field)
            if (idx >= 0) {
              freshErrors[idx].error = true
              freshErrors[idx].errorLabel = freshErrors[idx].errorLabel || ''
            }
          })
          setErrorTextfield(freshErrors)
        }
      }
    }
  }

  const handleRowClick = (onRowClickProperty, row, rowIndex, toggle = false) => {
    if (!onRowClickProperty) return
    const isExtraFilter = row[extraFilterParameter.name] === extraFilterParameter.value
    setSelectedRow(isExtraFilter ? null : rowIndex)
    if (isExtraFilter) {
      if (extraFilterParameterMessage) {
        showAlert({
          title: extraFilterParameterMessage,
          severity: 'warning',
          message: ''
        })
      }
      return
    }
    dismissAlert()

    // --- MODE TOGGLE ---
    if (toggle) {
      const isCurrentlySelected = selectedRow === rowIndex
      const newValue = isCurrentlySelected ? null : rowIndex
      setSelectedRow(newValue)

      if (typeof onRowClickProperty === 'function') {
        onRowClickProperty(isCurrentlySelected ? null : row)
      }

      return
    }

    if (typeof onRowClickProperty === 'function') {
      onRowClickProperty(row, rowIndex)
      setSelectedRow(rowIndex)
    }
  }

  // Historia 170 (CU8, RN34b, G1-10, S97-S102) — resolves `preselectedRecord` exactly ONCE, against
  // whatever the table's OWN first real data load turns out to be:
  //   · non-`pageable` (S97): search the WHOLE in-memory, filtered+sorted dataset (not just one page)
  //     for `field === value`, jump to its page and select it.
  //   · `pageable` (S98-S100, G1-10): `filterText` (above) already asked the server for
  //     `searchText` as `genericSearch`, so `data` IS the current page of results for that search —
  //     search it directly (no re-filtering; the server already did that) for `field === value`.
  // Not found in either mode (S100/S101): `onPreselectionResolved(null)`, nothing selected, no alert
  // of its own. Absent `preselectedRecord` (S106): this effect does nothing, ever.
  //
  // Selection itself is `setPage`+`setSelectedRow` DIRECTLY — the exact same pair `03-dominio.md`
  // points at as the precedent to generalize (`checkSelectedItemInData`, a few lines above) — never
  // `handleRowClick`, which early-returns without selecting anything when `onRowClick` is absent
  // (J4b: most of the ~14 destination windows select a row purely via custom action buttons, with no
  // `onRowClick` of their own, so gating the SELECTION on it would leave every one of them inert).
  // `onRowClick` is still invoked separately, if present, so a window that opens a detail on click
  // still does (S102) — same two effects a real click produces, just not through that function.
  const preselectionAttemptedRef = useRef(false)
  useEffect(() => {
    // `firstLoad` alone is not a reliable "the real data is here" signal: it can flip to `true` one
    // render before `data` itself catches up to the fetch that set it (observed empirically — the
    // effect fired once with `firstLoad: true` and `data` still `[]`). Gating on `data.length > 0`
    // instead waits for content that can actually be searched, for both modes.
    if (!preselectedRecord || preselectionAttemptedRef.current || data.length === 0) return
    preselectionAttemptedRef.current = true

    const selectFoundRow = (row, rowIndex) => {
      setSelectedRow(rowIndex)
      if (typeof onRowClick === 'function') onRowClick(row, rowIndex)
    }

    if (pageable) {
      const foundRow = data.find((row) => getObjectPropertyFromString(row, preselectedRecord.field) === preselectedRecord.value)
      if (foundRow) selectFoundRow(foundRow, data.indexOf(foundRow))
      onPreselectionResolved(foundRow || null)
      return
    }

    const filtered = getTableDataFiltered(data)
    const index = filtered.findIndex((row) => getObjectPropertyFromString(row, preselectedRecord.field) === preselectedRecord.value)
    if (index === -1) {
      onPreselectionResolved(null)
      return
    }
    setPage(Math.floor(index / rowsPerPage))
    selectFoundRow(filtered[index], index % rowsPerPage)
    onPreselectionResolved(filtered[index])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleColumnHeaderClick = (column) => {
    if (pageable) {
      const columnSortable = {}
      if (column.field === sortColumnFieldPageable.field) {
        columnSortable.field = sortColumnFieldPageable.field
        columnSortable.direction = sortColumnFieldPageable.direction * -1
      } else {
        columnSortable.field = column.field
        columnSortable.direction = -1
      }
      setSortColumnFieldPageable(columnSortable)
      // Historia 170 (S165/S52) — additive, opt-in (see the prop's own comment for why the default
      // stays `false`): sorting a different criterion makes the current page index mean something
      // else entirely, so tables that opt in return to page 0 whenever the user re-sorts.
      if (sortResetsPageableToZero) setPageableObject((previous) => ({ ...previous, number: 0 }))
    } else {
      if (column.field === sortColumnField) {
        setSortDirection(sortDirection * -1)
      } else {
        setSortColumnField(column.field)
        setSortDirection(-1)
      }
      if (column.sortOptions !== undefined) {
        column.sortOptions = column.sortOptions * -1
      } else {
        column.sortOptions = 1
      }
    }

    if (pageable) setLoadTable(!loadTable)
  }

  // Historia 127 (MIC-4248): the comparator lives in `tableRowSorting` so it can be tested without
  // mounting this table. Date columns compare the RAW instant of the row, not the painted text
  // `getObjectPropertyFromString` returns -- that text is what sorted 15/12/2025 after 02/01/2026.
  const getDataSorted = (dataToSort) => {
    if (actions && actions.edit && actions.edit.cancelSorting && editIndex > -2) return dataToSort
    return sortTableRows(dataToSort, {
      sortColumnField,
      sortDirection,
      columns,
      queueOrder,
      getColumnType,
      getCellValue: getObjectPropertyFromString
    })
  }

  const getTableDataSorted = () => {
    return getDataSorted(setMultiEdit !== undefined ? dataForMultipleEdit : data)
  }

  const getTableDataFilteredFromData = () => {
    return getTableDataFiltered(data)
  }

  const getTableDataFiltered = (rawData) => {
    return getTableDataSorted(rawData).filter((row) => {
      if (rowHasTableIndexDefined(row) && row[smartTableIndexKey] === editIndex) {
        if (editIndex === -1) {
          return false
        }
        return true
      }
      let filterResult = false
      let columnFilterCheck = true
      columns.forEach((column) => {
        let columnVal = getObjectPropertyFromString(row, column.field)
        columnVal = columnVal === undefined ? '' : columnVal
        const activeFilter = columnFilters.current[column.field]
        const activeOperator = columnFilterOperators.current[column.field]
        if (activeOperator === 'in') {
          // Label filter (filterByLabel): the row matches when its raw value is one of the resolved values.
          columnFilterCheck = columnFilterCheck && (!activeFilter || activeFilter.split('|').includes(columnVal.toString()))
        } else if (activeOperator) {
          // Date operator filter (gt/gte/lt/lte/between): compare as dates instead of substring.
          columnFilterCheck = columnFilterCheck && (!activeFilter || matchesDateOperator(parseDateTime(columnVal), activeFilter, activeOperator))
        } else {
          columnFilterCheck =
            columnFilterCheck &&
            (!activeFilter ||
              columnVal
                .toString()
                .toLowerCase()
                .indexOf(activeFilter.toLowerCase()) > -1)
        }

        if (
          typeof column.filterSearch === 'undefined' &&
          columnVal !== null &&
          columnVal.toString().toLowerCase().indexOf(filterText.toLowerCase()) > -1
        ) {
          filterResult = true
        }
        if (filterText !== '' && (pageable ? pageableObject.number !== 0 : page !== 0)) {
          pageableObject.number = 0
          setPage(0)
        }
      })
      return filterResult && columnFilterCheck
    })
  }

  const changeColumnFilter = (column, value) => {
    columnFilterChanged.current = [...columnFilterChanged.current, column.field]
    const prevColumnFilters = Object.assign(columnFilters.current)
    if (value !== '' && (page !== 0 || (pageable && pageableObject.number !== 0))) {
      pageableObject.number = 0
      if (!pageable) setPage(0)
    }

    columnFilters.current = { ...columnFilters.current, [column.field]: value }

    prevColumnFilters[column.field] = value
    // columnFilters.current = prevColumnFilters
    if (pageable) setLoadTable(!loadTable)
  }

  // Date column filters carry an operator (gt/gte/lt/lte/between) alongside the value.
  // Stable identities (useCallback with empty deps) so the memoized header popovers don't
  // re-render on every interval-driven data refresh. Applying/removing resets to the first
  // page and forces a reload so request and active indicator stay in sync.
  const applyColumnFilter = useCallback((field, operator, value) => {
    columnFilterChanged.current = [...columnFilterChanged.current, field]
    columnFilters.current = { ...columnFilters.current, [field]: value }
    columnFilterOperators.current = { ...columnFilterOperators.current, [field]: operator }
    setPage(0)
    setPageableObject((previous) => (previous.number === 0 ? previous : { ...previous, number: 0 }))
    setLoadTable((previous) => !previous)
  }, [])

  const removeColumnFilter = useCallback((field) => {
    columnFilterChanged.current = [...columnFilterChanged.current, field]
    const nextFilters = { ...columnFilters.current }
    const nextOperators = { ...columnFilterOperators.current }
    delete nextFilters[field]
    delete nextOperators[field]
    columnFilters.current = nextFilters
    columnFilterOperators.current = nextOperators
    setPage(0)
    setPageableObject((previous) => (previous.number === 0 ? previous : { ...previous, number: 0 }))
    setLoadTable((previous) => !previous)
  }, [])

  const handleHelperText = (object, limit, dataType) => {
    if (
      typeof limit === 'undefined' ||
      dataType === 'number' ||
      object === undefined
    ) {
      return ' '
    } else if (typeof object.length === 'undefined') {
      return `${object.toString().length} / ${limit}`
    } else {
      return `${object.length} / ${limit}`
    }
  }
  const handleValue = (value, defaultValue) => {
    return value
  }

  // Set default value when field value isn't filled on onBLur event
  const onBlurField = (value, row, field, defaultValue) => {
    if (
      (defaultValue !== null || defaultValue !== undefined) &&
      (value === '' || value === null || value === undefined)
    ) {
      row[field] = defaultValue
      return defaultValue
    }
    row[field] = value
    return value
  }

  const handleReadOnly = (row, columnIndex, columnField, readOnlyProperty, resetProperty) => {
    if (readOnlyProperty === undefined) return false
    let readOnly = false
    if (typeof readOnlyProperty === 'function') {
      const readOnlyFunction = (elem) => {
        readOnly = readOnlyProperty(elem)
      }
      readOnlyFunction(row)
    } else {
      readOnly = readOnlyProperty
    }

    handleReset(row, columnIndex, columnField, resetProperty)
    return readOnly
  }

  const setObjectValueByColumnField = (obj, columnField, value) => {
    let tempObj = obj
    const fields = columnField.split('.')
    for (let i = 0; i < fields.length - 1; i++) {
      const elem = fields[i]
      if (!tempObj[elem]) {
        tempObj[elem] = {}
      }
      tempObj = tempObj[elem]
    }
    tempObj[fields[fields.length - 1]] = value
  }

  const handleReset = (row, columnIndex, columnField, resetProperty) => {
    if (resetProperty === undefined) return false
    if (typeof resetProperty === 'function') {
      const resetFunction = (elem) => {
        const reset = resetProperty(elem)
        if (reset) {
          setObjectValueByColumnField(row, columnField, undefined)
          errorTextfield[columnIndex].error = false
          errorTextfield[columnIndex].errorLabel = ''
        }
      }
      resetFunction(row)
    }
  }

  const handleOnSelectAllDataClick = async (event) => {
    // Story 213 (S302, DK-2): what is covered has no action, so while a row is covered there is no "select all the data"
    // (it would take the covered rows into an export or a bulk action); it comes back once no row is covered.
    if (disableSelectAllData || getVisualizedData().some((row) => rowPlaceholder(row))) {
      return
    }
    let copyMultipleSelectionValue = multipleSelectionValue
    if (!event.target.checked && getVisualizedData().length !== 0) {
      copyMultipleSelectionValue.rows = []
      if (pageable) {
        copyMultipleSelectionValue = { ...copyMultipleSelectionValue, allSelected: true }
      } else {
        data.forEach(row => {
          const copyRow = reduceRowPropertiesToDesired(primaryKeys, row)
          copyMultipleSelectionValue.rows.push(copyRow)
        })
      }
      setSwitchBtwDataPageAndAllDataCheckbox(true)
      setNewMultipleSelectionValue(copyMultipleSelectionValue)
      return
    }
    setSwitchBtwDataPageAndAllDataCheckbox(false)
    resetMultiSelectionValue()
  }

  // Story 213 (S302): a row covered by `rowPlaceholder` is one cell across the row with the placeholder; nothing of the row
  // (no cell, no key from its data, no click, no checkbox, no action).
  const placeholderRow = (row, rowIndex) => (
    <TableRow key={rowIndex}>
      <TableCell colSpan={columnsForRender.length + (multipleSelection ? 1 : 0) + (actions ? 1 : 0)} sx={tableCellStyle}>
        {rowPlaceholder(row)}
      </TableCell>
    </TableRow>
  )

  const handleOnSelectAllDataPageClick = (event) => {
    if (event.target.checked && !handleIndeterminateCheckedStatus()) {
      let copyMultipleSelectionValue = multipleSelectionValue
      setSwitchBtwDataPageAndAllDataCheckbox(false)
      getVisualizedData().forEach((row) => {
        if (row[extraFilterParameter.name] !== extraFilterParameter.value && !rowPlaceholder(row)) {
          const copyRow = reduceRowPropertiesToDesired(primaryKeys, row)
          if (copyMultipleSelectionValue.rows.find(row => JSON.stringify(row) === JSON.stringify(copyRow)) === undefined) {
            copyMultipleSelectionValue = { ...copyMultipleSelectionValue, rows: [...copyMultipleSelectionValue.rows, copyRow] }
          }
        }
      })
      setNewMultipleSelectionValue(copyMultipleSelectionValue)
      return
    }
    resetMultiSelectionValue()
  }

  const selectedValuesMultiSelect = (row, column, isReadOnly = false) => {
    const editingRowProperty = getEditingRowProperty(column.field)
    const value = row[editingRowProperty.editingRowProperty]
    if (value === null || value === undefined) {
      return []
    } else if (Object.keys(value).length === 0 && Object.getPrototypeOf(value) === Object.prototype) {
      return []
    } else if (value !== undefined && value !== null && Array.isArray(value) === false) {
      if (isReadOnly) {
        return [value]
      } else {
        return [value[column.edit.field]]
      }
    } else if (value !== undefined && value !== null && Array.isArray(value)) {
      if (column.isSimpleData) {
        return value
      }
      if (isReadOnly) {
        return value
      } else {
        return value.map((opt) => opt[column.edit.field])
      }
    } else {
      return []
    }
  }

  const sourceWithSearch = useCallback(async (row, column, search) => {
    if (!column.edit || !column.edit.sourceWithSearch) {
      return
    }

    if (columnSelectWithSearchOptions &&
      columnSelectWithSearchOptions[column.name] &&
      columnSelectWithSearchOptions[column.name].searchQuery === search &&
      columnSelectWithSearchOptions[column.name].editIndex === editIndex
    ) {
      // Same search so we use previous data
      return optionValuesMultiSelect(columnSelectWithSearchOptions, column)
    }

    // New query load data
    const data = typeof column.edit.sourceWithSearch === 'function'
      ? await column.edit.sourceWithSearch(row, search)
      : (await apiGet(column.edit.sourceWithSearch,
          {
            columnFilters: [column.edit.field],
            columnFilterValues: [search],
            rows: [],
            page: 0,
            size: 10,
            allSelected: false
          })
        ).content

    setColumnSelectWithSearchOptions((oldValue) => {
      const newValue = { ...oldValue }
      newValue[column.name] = {
        id: column.edit.id,
        field: column.edit.field,
        searchQuery: search,
        editIndex,
        data
      }
      return newValue
    })

    const columnsData = {}
    columnsData[column.name] = { data }
    return optionValuesMultiSelect(columnsData, column)
  }, [columnSelectWithSearchOptions, editIndex])

  const optionValuesMultiSelect = (columnSelectOptions, column) => {
    // Always derive options from the current snapshot of columnSelectOptions
    // without relying on external reloads while selecting.
    const colOpts = columnSelectOptions?.[column.name]?.data
    if (!Array.isArray(colOpts) || colOpts.length === 0) {
      return []
    }
    // If the data is simple (array of strings), return it as is.
    if (column.isSimpleData) {
      return colOpts
    }

    // General case: map objects to strings using the field defined in the column.
    return colOpts.map(opt => opt?.[column.edit.field])
  }

  // Story 204 — RN1.9: a column with `filterByLabel: { options }` is filtered by its translated text. The options
  // (`{ value, label }`, value = what Sun stores) come from a fixed list or from the data the table already loads for the
  // column's edit dropdown (`getIntervalColumnSelectOptions`), never from a request of their own.
  // P17: a column that edits a related system value through a loaded list gets a label filter by itself.
  const columnTranslates = (column) => column.translate !== false
  const effectiveFilterByLabel = (column) => {
    if (column.filterByLabel) return column.filterByLabel
    if (!columnTranslates(column) || column.render || !column.edit?.source || column.edit?.sourceWithSearch || !isSeededRelationField(column.field)) return undefined
    const items = columnSelectOptions?.[column.name]?.data || []
    if (!hasSystemValueOptions(items, column.edit.field)) return undefined
    return { options: (loaded) => systemValueFilterOptions(t_, loaded, column.edit.field) }
  }

  const labelFilterOptions = (column) => {
    const spec = effectiveFilterByLabel(column)
    if (!spec) return []
    return typeof spec.options === 'function' ? spec.options(columnSelectOptions?.[column.name]?.data || []) : spec.options
  }

  const foldText = (text) => (text || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

  // The values whose translated label contains the typed text (case and accent insensitive).
  const labelFilterValues = (column, text) => {
    const needle = foldText(text).trim()
    if (needle === '') return []
    return labelFilterOptions(column).filter((option) => foldText(option.label).includes(needle)).map((option) => option.value)
  }

  // Typing over a `filterByLabel` column sends Sun the matching values with the `in` operator (`PENDING|WAITING`, pipe
  // like `between`). When no label matches, the text itself goes as the usual contains filter, so typing a raw code
  // keeps working.
  const changeLabelColumnFilter = (column, text) => {
    setLabelFilterTexts((previous) => ({ ...previous, [column.field]: text }))
    const values = labelFilterValues(column, text)
    columnFilterOperators.current = { ...columnFilterOperators.current, [column.field]: values.length > 0 ? 'in' : '' }
    changeColumnFilter(column, values.length > 0 ? values.join('|') : text)
  }

  // Story 204 — RN1.4: the visible text of an edit dropdown option; the stored value is still the option itself.
  // P17: without its own `optionLabel`, a system value option is translated by the table (tableSystemValues).
  const editOptionLabel = (column, option) => {
    const items = columnSelectOptions?.[column.name]?.data || []
    if (column.edit.optionLabel) return column.edit.optionLabel(option, items)
    return columnTranslates(column) && isSeededRelationField(column.field) ? optionSystemValueText(t_, option, items, column.edit.field) : option
  }

  // P17: the text of a read-only chip of a multi-select: the column's own optionLabel, or a seeded relation translated.
  // A row that is not being edited hands the chips its related rows whole; the label works on the edited field.
  const readOnlyChipLabel = (row, column, value) => {
    const option = value !== null && typeof value === 'object' ? value[column.edit.field] : value
    if (column.edit?.optionLabel) return editOptionLabel(column, option)
    return columnTranslates(column) ? multiSystemValueText(t_, row, column.field, option) : option
  }

  // P17: the read-mode text of a cell: a system value translated, anything else as before.
  const cellText = (row, column) => {
    const text = columnTranslates(column) ? cellSystemValueText(t_, row, column.field) : undefined
    return text !== undefined ? text : getObjectPropertyFromString(row, column.field)
  }

  // A column may narrow the options of a single-select per row (`edit.optionsForRow(row)` returns the allowed
  // option values), e.g. the legal destinations of a state depend on the state the row had when persisted.
  const optionValuesForRow = (columnSelectOptions, column, row) => {
    const options = optionValuesMultiSelect(columnSelectOptions, column)
    const allowed = typeof column.edit?.optionsForRow === 'function' ? column.edit.optionsForRow(row) : undefined
    return Array.isArray(allowed) ? options.filter((option) => allowed.includes(option)) : options
  }

  const resolveMultiSelectOptionIcon = useCallback((column, option) => {
    if (typeof column?.iconFunction !== 'function') {
      return undefined
    }

    if (option === null || option === undefined || typeof option === 'object') {
      return column.iconFunction(option)
    }

    const availableOptions = columnSelectOptions?.[column.name]?.data
    if (!Array.isArray(availableOptions)) {
      return column.iconFunction(option)
    }

    const optionObject = availableOptions.find(elem => elem?.[column.edit?.field] === option)
    return column.iconFunction(optionObject ?? option)
  }, [columnSelectOptions])

  const onChangeMultiSelect = (newSelectedValues, column, row, columnSelectOptions, columnIndex) => {
    const editingRowProperty = getEditingRowProperty(column.field)
    const copyErrorTextfield = [...errorTextfield]

    // Derive the new values according to the data type
    let newRowValues = []
    if (column.isSimpleData) {
      newRowValues = [...newSelectedValues]
    } else {
      const availableOptions = columnSelectOptions[column.name]?.data || []
      newRowValues = newSelectedValues
        .map((value) => availableOptions.find(elem => elem[column.edit.field] === value))
        .filter(Boolean)
    }

    if (setMultiEdit === undefined) {
      // Single-row edit mode: update state immutably
      const currentRow = row
      currentRow[editingRowProperty.editingRowProperty] = newRowValues

      if (mandatory(column, row)) {
        copyErrorTextfield[columnIndex].error = false
        copyErrorTextfield[columnIndex].errorLabel = ''
        setErrorTextfield(copyErrorTextfield)
      }

      if (column.condition !== undefined) {
        checkColumnsCondition(currentRow, newRowValues)
      }

      editingRowProperty.editingRow[editingRowProperty.editingRowProperty] = newRowValues
      setData((previousData) =>
        previousData.map((row) => {
          if (rowHasTableIndexDefined(row) && row[smartTableIndexKey] === editIndex) {
            return editingRowProperty.editingRow
          }
          return row
        })
      )

      if (column.onChange && typeof column.onChange === 'function') {
        column.onChange(editingRowProperty.editingRow)
      }
    } else {
      // Multiple edit mode: keep existing logic
      const rootProperty = column.field.split('.')[0]
      const copyDataForMultipleEdit = [...dataForMultipleEdit]
      copyDataForMultipleEdit.map((dataItem) => {
        if (row.id === dataItem.id) {
          dataItem[rootProperty] = newRowValues
        }
        return dataItem
      })
      setMultiEdit(copyDataForMultipleEdit)
      setDataForMultipleEdit(copyDataForMultipleEdit)

      if (mandatory(column, row)) {
        copyErrorTextfield[columnIndex].error = false
        copyErrorTextfield[columnIndex].errorLabel = ''
        setErrorTextfield(copyErrorTextfield)
      }
    }
  }

  const disableSaveButton = () => {
    if (forceDisableSaveButton) {
      return true
    }
    let returnValue = false
    errorTextfield.forEach((elem) => {
      if (elem.error) {
        returnValue = true
      }
    })
    return returnValue
  }

  const setFilterTextValue = (column) => {
    if (column.filter === undefined || columnFilterChanged.current.length > 0) {
      return
    }
    columnFilters.current[column.field] = column.filter
    return column.filter
  }

  const checked = (row) => {
    if (row[extraFilterParameter.name] === extraFilterParameter.value) {
      return false
    }
    const copyRow = reduceRowPropertiesToDesired(primaryKeys, row)
    const findRowOnMultipleSelectionValue = multipleSelectionValue.rows.find(row => JSON.stringify(row) === JSON.stringify(copyRow)) !== undefined

    if (multipleSelectionValue.allSelected) {
      return !findRowOnMultipleSelectionValue
    } else {
      return findRowOnMultipleSelectionValue
    }
  }

  const handleIndeterminateCheckedStatus = () => {
    if (pageable) {
      if (pageableObject.totalElements !== multipleSelectionValue.rows.length && multipleSelectionValue.rows.length > 0) { return true } else { return false }
    } else {
      if (data.length !== multipleSelectionValue.rows.length && multipleSelectionValue.rows.length > 0) {
        return true
      } else { return false }
    }
  }

  function filter (arr, criteria) {
    return arr.filter(function (obj) {
      return Object.keys(criteria).every(function (c) {
        return criteria[c] === obj[c].toString()
      })
    })
  }

  const getDisabledOpts = async (row, column) => {
    if (!column.disabledOptions) return []

    if (Array.isArray(column.disabledOptions)) return column.disabledOptions

    const result = await column.disabledOptions(row)
    return result
  }

  const getDatafetch = () => {
    if (pageable) {
      return dataFetch
    } else {
      const datafetchRows = []
      multipleSelectionValue.rows.forEach(element => {
        datafetchRows.push(filter(data, element)[0])
      })
      return datafetchRows
    }
  }

  const functionToChangeTheValueWhenAddANewElementByModal = (column, row) => {
    const copyRowCreatedInOtherSmartTable = { ...rowCreatedInOtherSmartTable }

    const copyErrorTextfield = [...errorTextfield]

    if (column.field.includes('.')) {
      row[column.field.split('.')[0]] = copyRowCreatedInOtherSmartTable
    } else {
      row[column.field] = copyRowCreatedInOtherSmartTable
    }
    const index = copyErrorTextfield.findIndex(item => item.field === column.field)
    copyErrorTextfield[index].error = false
    setErrorTextfield(copyErrorTextfield)
  }

  const [openFilters, setOpenFilters] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const closeFilters = (event) => {
    setOpenFilters(false)
  }

  const handleFilterToggleClick = (event) => {
    setAnchorEl(event.currentTarget)
    setOpenFilters((previousOpen) => !previousOpen)
  }

  const cancelOperation = () => {
    if (setDisableRowActions !== undefined) setDisableRowActions(false)
    showAlert({
      title: t_('Operation cancelled'),
      severity: 'info'
    })
  }

  /**
   * Handles the change of the queue order of a row
   * @param {*} row  The row to be moved
   * @param {Number} newValue  The new value of the queue order
   * @returns {Array} The updated data
   */
  const handleQueueOrderChange = (row, newValue) => {
    // Animate row change
    setIsAnimating(row.id)
    setTimeout(() => { setIsAnimating(null) }, 3000)

    // Change order
    const previousData = [...data]
    const currentIndex = previousData.findIndex((item) => item.id === row.id)
    if (currentIndex === -1) return previousData
    const newIndex = Math.max(0, Math.min(previousData.length - 1, Number(newValue) - 1))
    const dataCopy = [...previousData]
    const [movedItem] = dataCopy.splice(currentIndex, 1)
    dataCopy.splice(newIndex, 0, movedItem)
    const updatedData = dataCopy.map((item, index) => ({
      ...item,
      [queueOrder.field]: index + 1,
      'smart-table-index': index
    })).sort((a, b) => a[queueOrder.field] - b[queueOrder.field])
    return updatedData
  }

  const saveFetch = (elem) => {
    let fetchFunction
    if (queueOrder && queueOrder.field) { // If queue order is defined, update the order and fetch
      const newOrder = handleQueueOrderChange(elem, elem[queueOrder.field])
      fetchFunction = actions.edit.fetchFunction(elem, newOrder)
    } else {
      fetchFunction = actions.edit.fetchFunction(elem)
    }
    if (fetchFunction !== undefined) {
      fetchFunction
        .then(() => {
          showAlert({
            title: t_('Updated correctly.'),
            severity: 'success'
          })
        })
        .catch((error) => {
          showAlert({
            severity: error.name ? error.name : 'error',
            title: '',
            message: t_(error.message)
          })
        })
    }
  }

  const deleteFunction = async (row) => {
    if (setDisableRowActions !== undefined) setDisableRowActions(false)
    if (safeDeleteShowProperty !== null) {
      const response = await actions.delete.fetchFunction([row])
      setDeleteReponse(response)
      return
    }
    await actions.delete.fetchFunction(
      [row]
    ).then((params) => {
      if (params && params.disableAlert) {
        return
      }
      showAlert({
        title: t_('Deleted correctly.'),
        severity: 'success'
      })
    }).catch((error) => {
      showAlert({
        title: '',
        message: t_(error.message),
        severity: error.name
      })
    })
  }

  function setErrorValidation (errorTextfield, i, setErrorTextfield) {
    const copyErrorTexfield = [
      ...errorTextfield
    ]
    copyErrorTexfield[i].errorLabel = t_('Required *')
    copyErrorTexfield[i].error = true
    setErrorTextfield(copyErrorTexfield)
  }

  const tableRef = useRef()
  const tableContainerRef = useRef()
  const [toolbarMinWidth, setToolbarMinWidth] = useState('100%')

  useEffect(() => {
    const updateToolbarWidth = () => {
      const tableElement = tableRef.current
      const containerElement = tableContainerRef.current
      if (!tableElement || !containerElement) {
        setToolbarMinWidth('100%')
        return
      }

      const tableScrollWidth = Math.ceil(tableElement.scrollWidth || 0)
      const containerClientWidth = Math.ceil(containerElement.clientWidth || 0)
      const nextToolbarMinWidth = tableScrollWidth > containerClientWidth ? `${tableScrollWidth}px` : '100%'

      setToolbarMinWidth(previousValue => previousValue === nextToolbarMinWidth ? previousValue : nextToolbarMinWidth)
    }

    updateToolbarWidth()

    if (typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined') {
      // eslint-disable-next-line no-undef
      const resizeObserver = new ResizeObserver(() => {
        updateToolbarWidth()
      })

      if (tableRef.current) resizeObserver.observe(tableRef.current)
      if (tableContainerRef.current) resizeObserver.observe(tableContainerRef.current)

      return () => resizeObserver.disconnect()
    }

    window.addEventListener('resize', updateToolbarWidth)
    return () => window.removeEventListener('resize', updateToolbarWidth)
  }, [customColumns, data, sticky, searchAndPaginationVisible, toolbarDisplay, multipleSelection, queueOrder, actions])

  /**
   * Callback method of queueOrder buttons to handle the queue order of a row
   * @param {*} row The row to be moved
   * @param {String} direction The direction of the movement. It can be 'up' or 'down'
   */
  const handleQueueOrder = (row, direction) => {
    const directionMultiplier = sortColumnFieldPageable !== '' ? sortColumnFieldPageable.direction : sortDirection
    if (onQueueOrderChange !== undefined && typeof onQueueOrderChange === 'function') {
      onQueueOrderChange(row, direction, directionMultiplier)
    } else {
      const newOrder = handleQueueOrderChange(
        row,
        direction === 'up' ? row[queueOrder.field] - directionMultiplier : row[queueOrder.field] + directionMultiplier
      )
      setData([...newOrder])
      startTableInterval(false)
    }
  }

  /**
   * Method to render the queue order column
   * @param {*} row Row to visualize
   * @param {Number} index Index of the row
   * @param {Boolean} upDisabled If the up button should be disabled
   * @param {Boolean} downDisabled If the down button should be disabled
   * @param {function} onClickCallback Callback method to handle when buttons are clicked
   * @returns {JSX.Element} The JSX element to render the queue order column
   */
  const getQueueOrder = (row, index, upDisabled, downDisabled, onClickCallback) => {
    const OrderQueueColumnButton = ({ disabled = true, direction = 'up', index, row, onClickCallback }) => {
      const iconButtonStyle = {
        width: 0,
        paddingTop: 0,
        paddingBottom: 0
      }

      const enabledColor = theme.palette.primary.main
      const disabledColor = 'transparent'

      const getArrowKey = () => {
        const key = direction === 'down' ? 'ArrowDropDownKey' : 'ArrowDropUpkey'
        return key + index
      }

      const getArrowElement = () => {
        if (direction === 'down') {
          return <ArrowDropDown key={getArrowKey} sx={{ color: disabled ? disabledColor : enabledColor }} />
        }
        return <ArrowDropUp key={getArrowKey} sx={{ color: disabled ? disabledColor : enabledColor }} />
      }
      return (
        <IconButton sx={iconButtonStyle} disabled={disabled} onClick={() => onClickCallback(row, direction)}>
          {getArrowElement()}
        </IconButton>
      )
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {(rowHasTableIndexDefined(row) && row[smartTableIndexKey] === editIndex)
            ? (
              <TextField
                type='number'
                value={row[queueOrder.field]}
                onChange={(event) => {
                  row[queueOrder.field] = Number(event.target.value)
                }}
                helperText=' '
              />
              )
            : (
              <>
                <OrderQueueColumnButton
                  row={row}
                  disabled={upDisabled}
                  direction='up'
                  index={index}
                  onClickCallback={onClickCallback}
                />
                <Box key={'key' + index} style={{ textAlign: 'start' }}>
                  {row[queueOrder.field]}
                </Box>
                <OrderQueueColumnButton
                  row={row}
                  disabled={downDisabled}
                  direction='down'
                  index={index}
                  onClickCallback={onClickCallback}
                />
              </>
              )}
        </div>
      </div>
    )
  }

  const getBooleanType = (row, field, isReadOnly, rowEditing) => {
    const resolvedReadOnly = typeof isReadOnly === 'function' ? isReadOnly(row) : isReadOnly
    let value = getObjectPropertyFromString(row, field)
    if (typeof value === 'string') {
      if (value?.toLocaleLowerCase() === 'true') {
        value = true
      } else {
        value = false
      }
    }

    return (
      <>
        <IconButton
          disabled={resolvedReadOnly}
          onClick={() => {
            // Update the main data state
            setData((previousData) =>
              previousData.map((row) => {
                if (rowHasTableIndexDefined(row) && row[smartTableIndexKey] === editIndex) {
                  const newRow = { ...row }
                  newRow[field] = !row[field]
                  return newRow
                }
                return row
              })
            )

            // Toggle the boolean field in the current row
            row[field] = !row[field]
            if (setMultiEdit !== undefined) {
              // Add logic to update multi-editing variables
              const copyDataForMultipleEdit = [...dataForMultipleEdit]
              copyDataForMultipleEdit.map((dataItem) => {
                if (row.id === dataItem.id) {
                  dataItem[field] = row[field]
                }
                return dataItem
              })

              setMultiEdit(copyDataForMultipleEdit)
              setDataForMultipleEdit(copyDataForMultipleEdit)
            }
          }}
        >
          {value ? <Done color='primary' /> : <Close color='error' />}
        </IconButton>
        {rowEditing && !setMultiEdit ? <FormHelperText> </FormHelperText> : null}
      </>
    )
  }

  const getColumnNumericDefault = (column) => {
    if (!column) return null
    if (column.numeric && typeof column.numeric === 'object' && column.numeric.default !== undefined) {
      return column.numeric.default
    }
    return null
  }

  return (
    <SmartTablePaper>
      {AlertElement}
      <NonDeletableItemsDataShowAlert
        deleteResponse={deleteResponse}
        allItems={allFetchedItemsSet}
        property={safeDeleteShowProperty}
        allSelected={false}
      />
      <SmartDialog
        setOpen={openCreateDialog || openEditDialog}
        close={false}
        accept
        cancelCallback={() => { setOpenCreateDialog(false); setOpenEditDialog(false) }}
        acceptCallback={() => {
          saveFetch(currentSelectedRow)
          setOpenCreateDialog(false)
          setOpenEditDialog(false)
        }}
        cancel
        closeCallback={() => setOpenCreateDialog(false)}
        title={createDialogTitle({ color: 'primary', title: openCreateDialog ? t_('Create data?') : t_('Edit data?'), icon: DeleteOutlineRounded })}
        message={openCreateDialog ? t_('You are going to create new data, are you sure?') : t_('You are going to edit data, are you sure?')}
        renderComponent={
          <SmartMateriaUITable
            columns={columns}
            dataFetch={currentSelectedRow}
            searchAndPaginationVisible='none'
          />
        }
      />
      <SmartDialog
        setOpen={openTuneTable || tuneOpen}
        close={false}
        accept
        cancelCallback={() => { updateTableTuneFields(customColumns); setOpenTuneTable(false); onTuneClose() }}
        acceptCallback={() => { saveTableTuneFields(); setOpenTuneTable(false); onTuneClose() }}
        cancel
        closeCallback={() => { updateTableTuneFields(customColumns); setOpenTuneTable(false); onTuneClose() }}
        title={createDialogTitle({ color: 'primary', title, icon: TuneOutlined })}
        renderComponent={
          <TableTune
            fields={tableTuneFields}
            onChange={(newValue) => { setTableTuneFields(newValue) }}
          />
        }
      />
      <SmartDeleteDialog
        show={openDeleteDialog}
        cancelCallback={() => {
          cancelOperation()
          setOpenDeleteDialog(false)
        }}
        deleteCallBack={() => {
          deleteFunction(currentSelectedRow)
          setOpenDeleteDialog(false)
        }}
        rows={[currentSelectedRow]}
        columns={columns}
      />
      <div style={{ position: 'relative' }}>
        <div style={{
          position: sticky ? 'sticky' : 'initial',
          top: stickyTopValue,
          zIndex: 10,
          minWidth: sticky ? toolbarMinWidth : '100%',
          backgroundColor: theme.palette.background.color_373636
        }}
        >
          <SmartMaterialUIToolbar
            multipleSelectionValue={multipleSelectionValue}
            setMultipleSelectionValue={setMultipleSelectionValue}
            datafetch={getDatafetch()}
            title={title}
            setFilterText={setFilterText}
            filterText={filterText}
            actions={actions}
            columns={columns}
            editIndex={editIndex}
            messageToolbarMultipleSelection={messageToolbarMultipleSelection}
            searchAndPaginationVisible={searchAndPaginationVisible}
            toolbarDisplay={toolbarDisplay}
            smartTableIndexKey={smartTableIndexKey}
            toolbar={toolbar}
            multipleSelectionActions={multipleSelectionActions}
            deleteMultipleSelectionDisabled={deleteMultipleSelectionDisabled}
            setLoadTable={setLoadTable}
            loadTable={loadTable}
            setDisableRowActions={setDisableRowActions}
            onAddElementClick={onAddNewElementClick}
            resetPageCount={resetPageCount}
            pagination={requestPaginationMultiSelection}
            pageable={pageable}
            importFile={importFile}
            renderingFilters={filters
              ? () => <ParameterFilters
                  onToggleClick={
                  (toggleButton, filter) =>
                    onFilterToggleClick(toggleButton, filter)
                }
                  openFilters={openFilters}
                  filters={filters}
                  closeFilters={closeFilters}
                  handleClick={handleFilterToggleClick}
                  anchorEl={anchorEl}
                      />
              : null}
            importFileDisabled={importFileDisabled}
            toolBarStyle={toolBarStyle}
            safeDeleteShowProperty={safeDeleteShowProperty}
            allFetchedItemsSet={allFetchedItemsSet}
            tuneTable={tableTuneUniqueKey !== ''}
            openTuneTable={() => { setOpenTuneTable(true) }}
            requestPagination={requestPagination}
            originalDataFetch={dataFetch}
            hideExport={hideExport}
            compactHeader={compactHeader}
            searchPosition={searchPosition}
          />
        </div>
        <TableContainer ref={tableContainerRef} sx={{ overflow: 'initial', ...tableContainerStyle }}>
          {customColumns
            ? (
              <Table
                stickyHeader={sticky}
                size={dense ? 'small' : 'medium'}
                ref={tableRef}
                sx={{ width: '100%' }}
              >
                <TableHead sx={{
                  position: sticky ? 'sticky' : 'initial',
                  // Top is sticky offset + toolbar height.
                  top: stickyHeaderTopValue,
                  zIndex: 10,
                  backgroundColor: (theme) => theme.palette.background.color_373636
                }}
                >
                  {/* Search row from table head */}
                  <TableRow style={{ display: [searchDisplay !== undefined ? searchDisplay : searchAndPaginationVisible] }}>
                    {/* Add an empty cell on a table hade if is an  multiSelect */}
                    {multipleSelection
                      ? (
                        <TableCell sx={{ py: compactHeader ? 0.5 : undefined, px: compactHeader ? 1 : undefined }} />
                        )
                      : null}
                    {queueOrder && data.every(row => queueOrder.field in row) && !hideQueueButton
                      ? (
                        <TableCell sx={{ py: compactHeader ? 0.5 : undefined, px: compactHeader ? 1 : undefined }} />
                        )
                      : null}
                    {columnsForRender.map((column, index) => (
                      <React.Fragment key={'columnTable' + index}>
                        {
                          // Add a column if hasn't set to false hide property
                          !column.hide
                            // If the column search parameter is false, add an empty cell, otherwise add a search textField to that column
                            ? column.search === null
                              ? null
                              : column.search === false || column.disableFilter
                                ? <TableCell key='column_header_actions' sx={{ py: compactHeader ? 0.5 : undefined, px: compactHeader ? 1 : undefined }} />
                                : (
                                  <TableCell key={'filterRow_' + index} sx={{ py: compactHeader ? 0.5 : undefined, px: compactHeader ? 1 : undefined }}>
                                    {isDateFilterColumn(column)
                                      ? (
                                        <DateColumnFilterPopover
                                          column={column}
                                          storedValue={columnFilters.current[column.field]}
                                          storedOperator={columnFilterOperators.current[column.field]}
                                          onApply={applyColumnFilter}
                                          onClear={removeColumnFilter}
                                        />
                                        )
                                      : (
                                        <TextField
                                          variant='standard'
                                          sx={{ ...getTableSearchStyle(compactHeader), width: column.width }}
                                          // id={column.field}
                                          value={effectiveFilterByLabel(column) ? (labelFilterTexts[column.field] || '') : setFilterTextValue(column)}
                                          placeholder={t_('Search')}
                                          // Opt-in `searchable` keeps the filter usable when a column is not
                                          // sortable (e.g. columns backed by an aggregated/joined value that
                                          // cannot be server-side sorted). Columns without `searchable` keep
                                          // the original rule (search disabled unless sortable), so existing
                                          // sortable:false columns are unaffected.
                                          disabled={column.searchable ? false : !(column.sortable === undefined || column.sortable === true)}
                                          onChange={(event) => {
                                            if (effectiveFilterByLabel(column)) changeLabelColumnFilter(column, event.target.value)
                                            else changeColumnFilter(column, event.target.value)
                                          }}
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position='start'>
                                                <Search sx={getTableSearchIconStyle(compactHeader)} />
                                              </InputAdornment>
                                            )
                                          }}
                                        />
                                        )}
                                  </TableCell>
                                  )
                            : null
                        }
                      </React.Fragment>
                    ))}
                    {/* If the table has any actions, add an empty cell on the table head */}
                    {actions &&
                      ((actions.edit && (actions.edit.fetchFunction || actions.edit.redirect)) ||
                        (actions.custom && actions.custom.length > 0) || actions.accordion)
                      ? <TableCell key='column_header_actions' sx={{ py: compactHeader ? 0.5 : undefined, px: compactHeader ? 1 : undefined }} />
                      : null}
                  </TableRow>
                  {/* Optional grouped header row */}
                  {headerGroups && headerGroups.length > 0
                    ? (
                      <TableRow sx={{ backgroundColor: 'red' }}>
                        {multipleSelection ? <SmartTableHead compactHeader={compactHeader} /> : null}
                        {queueOrder && data.every(row => queueOrder.field in row) && !hideQueueButton ? <SmartTableHead compactHeader={compactHeader} /> : null}
                        {(() => {
                          const visibleColumns = columnsForRender
                          const groupIndexMap = new Map(headerGroups.map(g => [g.startIndex, g]))
                          const cells = []
                          let i = 0
                          while (i < visibleColumns.length) {
                            const grp = groupIndexMap.get(i)
                            if (grp) {
                              cells.push(
                                <SmartTableHead compactHeader={compactHeader} key={'group-cell-' + i} colSpan={grp.colSpan}>
                                  <Box sx={{ textAlign: 'center', borderBottom: theme => `2px solid ${theme.palette.primary.main}` }}>
                                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 600 }}>{grp.label}</Typography>
                                  </Box>
                                </SmartTableHead>
                              )
                              i += grp.colSpan
                            } else {
                              cells.push(<SmartTableHead compactHeader={compactHeader} key={'group-empty-' + i} />)
                              i++
                            }
                          }
                          if (actions && ((actions.edit && (actions.edit.fetchFunction || actions.edit.redirect)) || (actions.custom && actions.custom.length > 0) || actions.accordion)) {
                            cells.push(<SmartTableHead compactHeader={compactHeader} key='group-actions' />)
                          }
                          return cells
                        })()}
                      </TableRow>
                      )
                    : null}
                  {/* Column name row from table head */}
                  <TableRow>
                    {/* Add a global checkbox if the table has multi selection */}
                    {multipleSelection
                      ? (
                        <SmartTableHead compactHeader={compactHeader} padding='checkbox'>
                          <Checkbox
                            color='primary'
                            onDoubleClick={(event) => {
                              handleOnSelectAllDataClick(event)
                            }}
                            onClick={(event) => handleOnSelectAllDataPageClick(event)}
                            indeterminate={handleIndeterminateCheckedStatus()}
                            checkedIcon={switchBtwDataPageAndAllDataCheckbox ? <LibraryAddCheck color='primary' /> : <CheckBox />}
                            checked={pageable ? (multipleSelectionValue.allSelected && multipleSelectionValue.rows.length === 0) || multipleSelectionValue.rows.length === pageableObject.totalElements : multipleSelectionValue.rows.length === data.length}
                            inputProps={{ 'aria-labelledby': '' }}
                            disabled={editIndex > -2}
                          />
                        </SmartTableHead>
                        )
                      : null}
                    {queueOrder && data.every(row => queueOrder.field in row) && !hideQueueButton
                      ? (
                        <SmartTableHead compactHeader={compactHeader} key='smart-table-head-queue'>
                          <TableSortLabel
                            key='smart-table-sortLabel-queue'
                            active={queueOrder.field === (pageable ? sortColumnFieldPageable.field : sortColumnField)}
                            direction={(pageable ? sortColumnFieldPageable.direction : sortDirection) === -1 ? 'desc' : 'asc'}
                            onClick={() => handleColumnHeaderClick(queueOrder)}
                          >
                            {t_(queueOrder.title)}
                          </TableSortLabel>
                        </SmartTableHead>
                        )
                      : null}
                    {columnsForRender.map((column, index) => (
                      // Add a column that contains a sortable component if the column hasn't got a hide property
                      !column.hide
                        ? (
                          <SmartTableHead
                            compactHeader={compactHeader}
                            key={'smart-table-head-' + index}
                          >
                            {(column.sortable === undefined || column.sortable === true)
                              ? (
                                <TableSortLabel
                                  key={'smart-table-sortLabel-' + index}
                                  active={column.field === (pageable ? sortColumnFieldPageable.field : sortColumnField)}
                                  direction={(pageable ? sortColumnFieldPageable.direction : sortDirection) === -1 ? 'desc' : 'asc'}
                                  onClick={(event) =>
                                    handleColumnHeaderClick(column)}
                                  disabled={column.disableFilter}
                                >
                                  {column.name}
                                </TableSortLabel>)
                              : (column.name)}

                          </SmartTableHead>
                          )
                        : (null)
                    ))}
                    {/* If the table has any actions, add an empty cell on the table head */}
                    {actions &&
                      ((actions.edit && (actions.edit.fetchFunction || actions.edit.redirect)) ||
                        (actions.custom && actions.custom.length > 0) || actions.accordion)
                      ? (
                        <SmartTableHead compactHeader={compactHeader} key='smart-table-head-hola' />
                        )
                      : null}
                  </TableRow>

                </TableHead>
                {/* Here starts the table body */}
                <TableBody key='table_body'>
                  {/* If the table is still loading for the first time,show a CircularProgress icon */}
                  {!firstLoad
                    ? <TableRow><TableCell align='center' sx={{ paddingTop: '2rem', paddingBottom: '2rem' }} colSpan={multipleSelection ? columnsForRender.length + 1 : columnsForRender.length}><CircularProgress /></TableCell></TableRow>
                    : null}

                  {/* Loop getVisualizedData() method content and add a row per each record to the table body */}
                  {getVisualizedData().map((row, rowIndex) => (rowPlaceholder(row) && placeholderRow(row, rowIndex)) || (
                    <React.Fragment key={rowIndex}>
                      <SmartTableRow
                        hover
                        groupHover={hasSecondaryLine}
                        onClick={
                          onRowClick
                            ? () => handleRowClick(onRowClick, row, rowIndex, rowToggle)
                            : null
                        }
                        row={rowIndex % 2 !== 0}
                        key={'data' + rowIndex}
                        backgroundColor={rowBackgroundColorFunction && typeof rowBackgroundColorFunction === 'function' ? rowBackgroundColorFunction(row) : null}
                        onMouseEnter={hasSecondaryLine ? () => setHoveredRecordIndex(rowIndex) : undefined}
                        onMouseLeave={hasSecondaryLine ? () => setHoveredRecordIndex(-1) : undefined}
                        selected={
                          (row && rowHasTableIndexDefined(row) &&
                            editIndex === row[smartTableIndexKey]) ||
                          selectedRow === rowIndex ||
                          (hasSecondaryLine && hoveredRecordIndex === rowIndex)
                        }
                        rowChange={row.id !== null ? isAnimating === row.id : false}
                      >
                        {/* Add checkBox to the table body row if multiSelection parameter is set */}
                        {multipleSelection
                          ? (
                            <TableCell padding='checkbox' sx={tableCellStyle}>
                              <>
                                <Checkbox
                                  color='primary'
                                  onChange={() => handleOnChange(row)}
                                  checked={checked(row)}
                                  inputProps={{
                                    'aria-labelledby': ''
                                  }}
                                  disabled={row[extraFilterParameter.name] === extraFilterParameter.value || editIndex > -2}
                                />
                                {editIndex > -2 ? <FormHelperText> </FormHelperText> : null}
                              </>
                            </TableCell>
                            )
                          : null}
                        {queueOrder && data.every(row => queueOrder.field in row) && !hideQueueButton
                          ? (
                            <TableCell padding='checkbox' sx={tableCellStyle}>
                              {getQueueOrder(
                                row,
                                rowIndex,
                                rowIndex === 0 || queueOrder.disabled,
                                rowIndex === getVisualizedData().length - 1 || queueOrder.disabled,
                                handleQueueOrder)}
                            </TableCell>
                            )
                          : null}
                        {columnsForRender.map((column, columnIndexUnsafe) => {
                          // Add columns to the table body row if the column hide parameter is not set
                          const resolvedColumnIndex = columns.findIndex(c => c.field === column.field)
                          const columnIndex = resolvedColumnIndex >= 0 ? resolvedColumnIndex : columnIndexUnsafe

                          return !column.hide
                            ? (
                              // key uses the render-position index (unique per row); columns.findIndex(by field)
                              // collides when a screen declares two columns with the same `field` (e.g. Part 'status').
                              <TableCell key={'data' + rowIndex + '_column' + columnIndexUnsafe} sx={column.style ? { ...tableCellStyle, ...column.style } : tableCellStyle}>
                                <Box sx={{ paddingTop: dense && editIndex >= -1 ? '1em' : '' }}>
                                  {/* If the content is not and object or it can't retrieve the data from the column check if it the column has an render parameter set and show it.
                                    Otherwise print null */}
                                  {typeof getObjectPropertyFromString(
                                    row,
                                    column.field
                                  ) !== 'object' || getObjectPropertyFromString(row, column.field) || getObjectPropertyFromString(row, column.field) === null
                                    ? (
                                        column.render && !((typeof column.renderOnlyInReadMode === 'function' ? column.renderOnlyInReadMode(row) : column.renderOnlyInReadMode) && ((rowHasTableIndexDefined(row) && editIndex === row[smartTableIndexKey]) || (setMultiEdit !== undefined && !column.readOnly)))
                                          ? (column.render(row, { showAlert }))
                                          : (rowHasTableIndexDefined(row) && editIndex === row[smartTableIndexKey]) || (setMultiEdit !== undefined && !column.readOnly)
                                              ? (
                                                  (// Check if the content must be shown as readOnly. Condition = Not creating a new row and column edit property is settrue, or not creating and check reandOnly propery
                                                    Object.prototype.hasOwnProperty.call(column, 'edit') &&
                                              !column.edit &&
                                              editIndex !== -1) || ((typeof column.readOnly === 'function' || editIndex !== -1) && handleReadOnly(
                                                    row,
                                                    columnIndex,
                                                    column.field,
                                                    column.readOnly,
                                                    column.reset
                                                  )) // When the content is going to display as read only check if it is an autocomplete type. It it's not, display the content as a default textbox, otherwise use the autocomplete component as read only
                                                    ? (getColumnType(column, row) === 'boolean')
                                                        ? getBooleanType(row, column.field, true, true)
                                                        : (getColumnType(column, row) !== 'smartMultiSelect'
                                                            ? <TextField
                                                                value={cellText(row, column)}
                                                                variant='standard'
                                                                sx={{ ...(setMultiEdit === undefined ? textFieldUnderlineStyle : {}), width: column.width }}
                                                                InputProps={setMultiEdit !== undefined ? { disableUnderline: true } : null}
                                                                InputLabelProps={{
                                                                  shrink: true
                                                                }}
                                                                helperText={setMultiEdit !== undefined ? null : ' '}
                                                                autoComplete='Off'
                                                              />
                                                            : <SmartSelect
                                                                readOnly
                                                                multiple
                                                                width={column.width}
                                                                value={selectedValuesMultiSelect(row, column)}
                                                                disabledOptions={getDisabledOpts(row, column)}
                                                                sortValues={column.sortValues}
                                                                optionLabelTransform={(value) => readOnlyChipLabel(row, column, value)}
                                                                sourceWithSearch={column.edit?.sourceWithSearch ? async (search) => await sourceWithSearch(row, column, search) : undefined}
                                                              />) // From here above the desired component to be edited will be select for the type defined
                                                    : (getColumnType(column, row) === 'boolean')
                                                        ? getBooleanType(row, column.field, column.readOnly, true)
                                                        : (Object.prototype.hasOwnProperty.call(column, 'edit') &&
                                                  getColumnType(column, row) === 'smartMultiSelect' && columnSelectOptions[column.name] &&
                                                  columnSelectOptions[column.name].data)
                                                            ? <SmartSelect
                                                                multiple
                                                                disableClearable={false}
                                                                functionToChangeTheValueWhenAddANewElement={() => functionToChangeTheValueWhenAddANewElementByModal(column, row)}
                                                                setReloadAutocompleteOptions={setReloadAutocompleteOptions}
                                                                value={selectedValuesMultiSelect(row, column)}
                                                                disabledOptions={getDisabledOpts(row, column)}
                                                                selectableOptions={optionValuesMultiSelect(columnSelectOptions, column)}
                                                                optionLabelTransform={(option) => editOptionLabel(column, option)}
                                                                textFieldLabel={column.name}
                                                                content={column.content}
                                                                addButtonVisible={column.addButtonVisible}
                                                                addDialogTitle={column.addDialogTitle}
                                                                addDialogAccept={column.addDialogAccept}
                                                                addDialogClicked={column.addDialogClicked}
                                                                onAddDialogAccept={column.handleAddDialogAccept}
                                                                disableAddDialogButtons={column.disableAddDialogButtons}
                                                                addDialogClose={column.addDialogClose}
                                                                sortValues={column.sortValues}
                                                                iconFunction={(option) => resolveMultiSelectOptionIcon(column, option)}
                                                                sourceWithSearch={column.edit?.sourceWithSearch ? async (search) => await sourceWithSearch(row, column, search) : undefined}
                                                                label={errorTextfield[columnIndex].error
                                                                  ? (
                                                                    <Tooltip placement='top' title={errorTextfield[columnIndex].errorLabel}>
                                                                      <WarningIcon />
                                                                    </Tooltip>
                                                                    )
                                                                  : handleLabel(
                                                                    column,
                                                                    row,
                                                                    columnIndex
                                                                  )}
                                                                InputLabelProps={{
                                                                  shrink: true
                                                                }}
                                                                onChange={(newValues) => {
                                                                  onChangeMultiSelect(newValues, column, row, columnSelectOptions, columnIndex)
                                                                  if (column.edit.onClick_render) { column.edit.onClick_render(row, newValues) }
                                                                }}
                                                                onClose={(newValue) => {
                                                                  if (column.edit.onClose_render) { column.edit.onClose_render(row, newValue) }
                                                                }}
                                                                error={
                                                      errorTextfield[columnIndex].error
                                                        ? errorTextfield[columnIndex].error
                                                        : null
                                                    }
                                                                helperText=' '
                                                              />
                                                            : (Object.prototype.hasOwnProperty.call(column, 'edit') &&
                                                    getColumnType(column, row) === 'smartSelect')
                                                                ? (
                                                                  <div>
                                                                    <SmartSelect
                                                                      multiple={false}
                                                                      functionToChangeTheValueWhenAddANewElement={() => functionToChangeTheValueWhenAddANewElementByModal(column, row)}
                                                                      setReloadAutocompleteOptions={setReloadAutocompleteOptions}
                                                                      value={handleSelectValue(row, column)}
                                                                      disabledOptions={getDisabledOpts(row, column)}
                                                                      selectableOptions={optionValuesForRow(columnSelectOptions, column, row)}
                                                                      optionLabelTransform={(option) => editOptionLabel(column, option)}
                                                                      textFieldLabel={column.name}
                                                                      content={column.edit.content}
                                                                      addButtonVisible={column.edit.addButtonVisible}
                                                                      addDialogTitle={column.edit.addDialogTitle}
                                                                      addDialogAccept={column.edit.addDialogAccept}
                                                                      onAddDialogAccept={column.edit.handleAddDialogAccept}
                                                                      disableAddDialogButtons={column.edit.disableAddDialogButtons}
                                                                      addDialogClose={column.edit.addDialogClose}
                                                                      autoSelectFirst={column.autoSelectFirst}
                                                                      sortValues={column.sortValues}
                                                                      label={errorTextfield[columnIndex].error
                                                                        ? (
                                                                          <Tooltip placement='top' title={errorTextfield[columnIndex].errorLabel}>
                                                                            <WarningIcon />
                                                                          </Tooltip>
                                                                          )
                                                                        : handleLabel(column, row, columnIndex)}
                                                                      InputLabelProps={{
                                                                        shrink: true
                                                                      }}
                                                                      onChange={(newValue) => {
                                                                        changeSelectValue(newValue, column, columnIndex, row)
                                                                        if (column.edit.onClick_render) { column.edit.onClick_render(row, newValue) }
                                                                      }}
                                                                      onClose={(newValue) => {
                                                                        if (column.edit.onClose_render) { column.edit.onClose_render(row, newValue) }
                                                                      }}
                                                                      helperText={setMultiEdit ? undefined : ' '}
                                                                      error={errorTextfield[columnIndex].error ? errorTextfield[columnIndex].error : null}
                                                                      disableClearable={mandatory(column, row, columnIndex) !== undefined}
                                                                      width={column.width}
                                                                      toolTip={column.edit.toolTip}
                                                                      toolTipValue={column.edit.toolTipValue}
                                                                      sourceWithSearch={column.edit?.sourceWithSearch ? async (search) => await sourceWithSearch(row, column, search) : undefined}
                                                                    />
                                                                  </div>)
                                                                : Object.prototype.hasOwnProperty.call(column, 'edit') &&
                                                      (getColumnType(column, row) === 'select' ||
                                                        getColumnType(column, row) === 'select-plain')
                                                                  ? (
                                                                    <div>
                                                                      <TextField
                                                                        select
                                                                        variant='outlined'
                                                                        style={{ minWidth: '6em' }}
                                                                        label={errorTextfield[columnIndex].error
                                                                          ? (
                                                                            <Tooltip placement='top' title={errorTextfield[columnIndex].errorLabel}>
                                                                              <WarningIcon />
                                                                            </Tooltip>
                                                                            )
                                                                          : handleLabel(
                                                                            column,
                                                                            row,
                                                                            columnIndex
                                                                          )}
                                                                        InputLabelProps={{
                                                                          shrink: true
                                                                        }}
                                                                        key={
                                                                          'select' +
                                                                          rowIndex +
                                                                          '_' +
                                                                          columnIndex
                                                                        }
                                                                        error={
                                                                          errorTextfield[columnIndex].error
                                                                            ? errorTextfield[columnIndex].error
                                                                            : null
                                                                        }
                                                                        value={handleSelectValue(row, column)}
                                                                        onChange={(event) => {
                                                                          changeSelectValue(
                                                                            event.target.value,
                                                                            column,
                                                                            columnIndex,
                                                                            row
                                                                          )
                                                                        }}
                                                                        onClick={column.edit.onClick_render ? (event) => column.edit.onClick_render(row) : null}
                                                                      >
                                                                        {[
                                                                          !mandatory(column, row, columnIndex)
                                                                            ? (
                                                                              <MenuItem key={'none1' + columnIndex} value='None'>
                                                                                <em>{t_('None')}</em>
                                                                              </MenuItem>
                                                                              )
                                                                            : null,
                                                                          columnSelectOptions[column.name] &&
                                                                columnSelectOptions[column.name].data
                                                                            ? getColumnType(column, row) === 'select-plain'
                                                                              ? columnSelectOptions[
                                                                                column.name
                                                                              ].data.map((opt, index) => (
                                                                                <MenuItem
                                                                                  key={
                                                                                    'select' +
                                                                                    index +
                                                                                    rowIndex +
                                                                                    '_' +
                                                                                    columnIndex +
                                                                                    '_' +
                                                                                    opt
                                                                                  }
                                                                                  value={opt}
                                                                                >
                                                                                  {editOptionLabel(column, opt)}
                                                                                </MenuItem>
                                                                              ))
                                                                              : columnSelectOptions[
                                                                                column.name
                                                                              ].data.map((opt, index) => {
                                                                                return (
                                                                                  <MenuItem
                                                                                    key={
                                                                                      'select2' +
                                                                                      index +
                                                                                      rowIndex +
                                                                                      '_' +
                                                                                      columnIndex +
                                                                                      '_' +
                                                                                      opt[column.edit.id]
                                                                                    }
                                                                                    value={
                                                                                      opt[column.edit.field]
                                                                                    }
                                                                                  >
                                                                                    {editOptionLabel(column, opt[column.edit.field])}
                                                                                  </MenuItem>
                                                                                )
                                                                              })
                                                                            : null]}
                                                                      </TextField>
                                                                      <FormHelperText>{' '}
                                                                      </FormHelperText>
                                                                    </div>
                                                                    ) // Multi select type.
                                                                  : (Object.prototype.hasOwnProperty.call(column, 'edit') &&
                                                        getColumnType(column, row) === 'smartMultiSelect' && columnSelectOptions[column.name] &&
                                                        columnSelectOptions[column.name].data
                                                                      ? (
                                                                        <div>
                                                                          <SmartSelect
                                                                            readOnly={false}
                                                                            multiple
                                                                            width={column.width}
                                                                            value={selectedValuesMultiSelect(row, column)}
                                                                            disabledOptions={getDisabledOpts(row, column)}
                                                                            selectableOptions={optionValuesMultiSelect(columnSelectOptions, column)}
                                                                            optionLabelTransform={(option) => editOptionLabel(column, option)}
                                                                            sourceWithSearch={column.edit?.sourceWithSearch ? async (search) => await sourceWithSearch(row, column, search) : undefined}
                                                              // parameterName={getEditingRowProperty(column.field).editingRowProperty}
                                                              // columnIndex={columnIndex}
                                                                            errorTextfield={errorTextfield}
                                                                            onChange={(newValues) => {
                                                                              onChangeMultiSelect(newValues, column, row, columnSelectOptions, columnIndex)
                                                                              if (column.edit.onClick_render) { column.edit.onClick_render(row, newValues) }
                                                                            }}
                                                                            onClose={(newValue) => {
                                                                              if (column.edit.onClose_render) { column.edit.onClose_render(row, newValue) }
                                                                            }}
                                                                            sortValues={column.sortValues}
                                                                            iconFunction={(option) => resolveMultiSelectOptionIcon(column, option)}
                                                                          />
                                                                          <FormHelperText
                                                                            error={
                                                                              errorTextfield[columnIndex].error
                                                                                ? errorTextfield[columnIndex].error
                                                                                : null
                                                                            }
                                                                          >
                                                                            {errorTextfield[columnIndex].error
                                                                              ? errorTextfield[columnIndex]
                                                                                .errorLabel
                                                                              : handleLabel(
                                                                                column,
                                                                                row,
                                                                                columnIndex
                                                                              )}
                                                                          </FormHelperText>
                                                                        </div>
                                                                        )
                                                                      : (getColumnType(column, row) === 'dateTimePicker' || getColumnType(column, row) === 'datePicker'
                                                                          ? (getColumnType(column, row) === 'dateTimePicker')
                                                                              ? (
                                                                                <>
                                                                                  <DateTimePicker
                                                                                    sx={{ width: '12rem' }}
                                                                                    label={
                                                                                      errorTextfield[columnIndex].error
                                                                                        ? (
                                                                                          <Tooltip placement='top' title={errorTextfield[columnIndex].errorLabel}>
                                                                                            <WarningIcon />
                                                                                          </Tooltip>
                                                                                          )
                                                                                        : handleLabel(column, row, columnIndex)
                                                                                    }
                                                                                    value={
                                                                                      getObjectPropertyFromString(row, column.field, null) === null
                                                                                        ? null
                                                                                        : getDateTimeFromString(row, column.field, null)
                                                                                    }
                                                                                    slotProps={{
                                                                                      actionBar: {
                                                                                        actions: ['cancel', 'accept', 'clear']
                                                                                      },
                                                                                      textField: {
                                                                                        variant: 'outlined',
                                                                                        InputLabelProps: { shrink: true },
                                                                                        error: errorTextfield[columnIndex].error,
                                                                                        helperText: errorTextfield[columnIndex].error
                                                                                          ? errorTextfield[columnIndex].error
                                                                                          : ''
                                                                                      }
                                                                                    }}
                                                                                    localeText={{
                                                                                      cancelButtonLabel: t_('CANCEL'),
                                                                                      okButtonLabel: t_('ACCEPT'),
                                                                                      clearButtonLabel: t_('CLEAR')
                                                                                    }}
                                                                                    closeOnSelect={false}
                                                                                    showToolbar={false}
                                                                                    format={format.dateTime}
                                                                                    ampm={false}
                                                                                    timeSteps={{ minutes: 1 }}
                                                                                    onChange={(value) => {
                                                                                      changeTextValue(value, column, columnIndex, row)
                                                                                    }}
                                                                                  />
                                                                                  <FormHelperText
                                                                                    error={
                                                                                      errorTextfield[columnIndex].error
                                                                                        ? errorTextfield[columnIndex].error
                                                                                        : null
                                                                                    }
                                                                                  >
                                                                                    {' '}
                                                                                  </FormHelperText>
                                                                                </>
                                                                                )
                                                                              : (
                                                                                <>
                                                                                  <DatePicker
                                                                                    sx={{ width: '12rem' }}
                                                                                    label={
                                                                                      errorTextfield[columnIndex].error
                                                                                        ? errorTextfield[columnIndex].errorLabel
                                                                                        : handleLabel(column, row, columnIndex)
                                                                                    }
                                                                                    value={
                                                                                      getObjectPropertyFromString(row, column.field, null) === null
                                                                                        ? null
                                                                                        : getDateFromString(row, column.field, null)
                                                                                    }
                                                                                    slotProps={{
                                                                                      actionBar: {
                                                                                        actions: ['cancel', 'accept', 'clear']
                                                                                      },
                                                                                      textField: {
                                                                                        variant: 'outlined',
                                                                                        InputLabelProps: { shrink: true },
                                                                                        error: Boolean(errorTextfield[columnIndex].error),
                                                                                        helperText: errorTextfield[columnIndex].error
                                                                                          ? errorTextfield[columnIndex].error
                                                                                          : ''
                                                                                      }
                                                                                    }}
                                                                                    localeText={{
                                                                                      cancelButtonLabel: t_('CANCEL'),
                                                                                      okButtonLabel: t_('ACCEPT'),
                                                                                      clearButtonLabel: t_('CLEAR')
                                                                                    }}
                                                                                    closeOnSelect={false}
                                                                                    showToolbar={false}
                                                                                    format={format.date}
                                                                                    timeSteps={{ minutes: 1 }}
                                                                                    onChange={(value) => {
                                                                                      if (value instanceof Date && !isNaN(value)) {
                                                                                        const dateStr = `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
                                                                                        changeTextValue(dateStr, column, columnIndex, row)
                                                                                      } else {
                                                                                        changeTextValue(value, column, columnIndex, row)
                                                                                      }
                                                                                    }}
                                                                                  />

                                                                                  <FormHelperText
                                                                                    error={
                                                                                      errorTextfield[columnIndex].error
                                                                                        ? errorTextfield[columnIndex].error
                                                                                        : null
                                                                                    }
                                                                                  >
                                                                                    {' '}
                                                                                  </FormHelperText>
                                                                                </>
                                                                                )// Default type
                                                                          : (
                                                                              getColumnType(column, row) === 'file'
                                                                                ? (
                                                                                  <Stack direction='row' alignItems='stretch' spacing={1} sx={{ width: '100%' }}>
                                                                                    <TextField
                                                                                      onDragOver={(e) => {
                                                                                        e.preventDefault()
                                                                                        e.stopPropagation()
                                                                                        e.currentTarget.classList.add('drag-active')
                                                                                      }}
                                                                                      onDragLeave={(e) => {
                                                                                        e.preventDefault()
                                                                                        e.stopPropagation()
                                                                                        e.currentTarget.classList.remove('drag-active')
                                                                                      }}
                                                                                      onDrop={(e) => {
                                                                                        e.preventDefault()
                                                                                        e.stopPropagation()
                                                                                        e.currentTarget.classList.remove('drag-active')

                                                                                        const file = e.dataTransfer.files?.[0]
                                                                                        if (file) {
                                                                                          column.edit.onFileSelected(row, file)
                                                                                        }
                                                                                      }}
                                                                                      onClick={() => {
                                                                                        if (column?.edit?.onOpen && typeof column.edit.onOpen === 'function') {
                                                                                          column.edit.onOpen(row)
                                                                                        }
                                                                                        const input = document.createElement('input')
                                                                                        input.type = 'file'
                                                                                        input.style.display = 'none'
                                                                                        input.onchange = (e) => {
                                                                                          const file = e.target.files?.[0]
                                                                                          if (!file) return
                                                                                          changeTextValue(file.name, column, columnIndex, row)
                                                                                          if (column?.edit?.onFileSelected && typeof column.edit.onFileSelected === 'function') {
                                                                                            column.edit.onFileSelected(row, file)
                                                                                          }
                                                                                        }
                                                                                        document.body.appendChild(input)
                                                                                        input.click()
                                                                                        setTimeout(() => {
                                                                                          document.body.removeChild(input)
                                                                                        }, 0)
                                                                                      }}
                                                                                      value={getObjectPropertyFromString(row, column.field) || ''}
                                                                                      placeholder={column.placeholder || ''}
                                                                                      label={
                                                                                        errorTextfield[columnIndex].error
                                                                                          ? <Tooltip placement='top' title={errorTextfield[columnIndex].errorLabel}><WarningIcon /></Tooltip>
                                                                                          : handleLabel(
                                                                                            column,
                                                                                            row,
                                                                                            columnIndex
                                                                                          )
                                                                                      }
                                                                                      variant='outlined'
                                                                                      size='medium'
                                                                                      sx={{
                                                                                        width: column.width,
                                                                                        flex: 1,
                                                                                        minWidth: 0,
                                                                                        transition: '0.2s',
                                                                                        // When the class we added above is present:
                                                                                        '&.drag-active': {
                                                                                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                                                                          '& .MuiOutlinedInput-root': {
                                                                                            '& fieldset': {
                                                                                              borderColor: 'primary.main',
                                                                                              borderWidth: '2px',
                                                                                              borderStyle: 'dashed' // Dashed lines look great for upload zones
                                                                                            }
                                                                                          }
                                                                                        }
                                                                                      }}
                                                                                      InputProps={{
                                                                                        readOnly: true,
                                                                                        startAdornment: (
                                                                                          <InputAdornment position='start'>
                                                                                            <InsertDriveFileRounded color='primary' />
                                                                                          </InputAdornment>
                                                                                        )
                                                                                      }}
                                                                                      error={Boolean(errorTextfield[columnIndex].error)}
                                                                                      helperText={handleHelperText(
                                                                                        getObjectPropertyFromString(row, column.field),
                                                                                        column.charLimit,
                                                                                        'text'
                                                                                      )}
                                                                                      InputLabelProps={{
                                                                                        shrink: true
                                                                                      }}
                                                                                      inputProps={{
                                                                                        maxLength: column.charLimit
                                                                                      }}
                                                                                    />
                                                                                  </Stack>
                                                                                  )
                                                                                : (
                                                                                  <TextField
                                                                                    sx={
                                                                                    {
                                                                                      ...textFieldStyle,
                                                                                      width: column.width
                                                                                    }
                                                                                  }
                                                                                    key={setMultiEdit === undefined
                                                                                      ? 'textfield' + column.name
                                                                                      : handleValue(
                                                                                        getObjectPropertyFromString(
                                                                                          row,
                                                                                          column.field
                                                                                        ),
                                                                                        getColumnNumericDefault(column) !== null
                                                                                          ? getColumnNumericDefault(column)
                                                                                          : null
                                                                                      )}
                                                                                    label={
                                                                                    errorTextfield[columnIndex].error
                                                                                      ? <Tooltip placement='top' title={errorTextfield[columnIndex].errorLabel}><WarningIcon /></Tooltip>
                                                                                      : handleLabel(
                                                                                        column,
                                                                                        row,
                                                                                        columnIndex
                                                                                      )
                                                                                  }
                                                                                    error={
                                                                                    errorTextfield[columnIndex].error
                                                                                      ? errorTextfield[columnIndex].error
                                                                                      : null
                                                                                  }
                                                                                    {...(column.decimals !== undefined
                                                                                      ? {
                                                                                          value: handleValue(
                                                                                            getObjectPropertyFromString(
                                                                                              row,
                                                                                              column.field
                                                                                            ),
                                                                                            column.numeric &&
                                                                                          column.numeric.default !== undefined
                                                                                              ? column.numeric.default
                                                                                              : null
                                                                                          )
                                                                                        }
                                                                                      : {
                                                                                          defaultValue: handleValue(
                                                                                            getObjectPropertyFromString(
                                                                                              row,
                                                                                              column.field
                                                                                            ),
                                                                                            column.numeric &&
                                                                                          column.numeric.default !== undefined
                                                                                              ? column.numeric.default
                                                                                              : null
                                                                                          )
                                                                                        })}
                                                                                    onBlur={setMultiEdit !== undefined
                                                                                      ? (event) => changeTextValue(
                                                                                          event.target.value,
                                                                                          column,
                                                                                          columnIndex,
                                                                                          row)
                                                                                      : () =>
                                                                                          onBlurField(
                                                                                            getObjectPropertyFromString(
                                                                                              row,
                                                                                              column.field
                                                                                            ),
                                                                                            row,
                                                                                            column.field,
                                                                                            column.numeric &&
                                                                                          column.numeric.default !== undefined
                                                                                              ? column.numeric.default
                                                                                              : null
                                                                                          )}
                                                                                    placeholder={
                                                                                    column.placeholder == null
                                                                                      ? ''
                                                                                      : column.placeholder
                                                                                  }
                                                                                    InputLabelProps={{
                                                                                      shrink: true
                                                                                    }}
                                                                                    inputProps={{
                                                                                      maxLength: column.charLimit
                                                                                    }}
                                                                                    type={evaluateColumnNumeric(column, row) ? 'number' : 'text'}
                                                                                    helperText={setMultiEdit !== undefined
                                                                                      ? null
                                                                                      : handleHelperText(
                                                                                        getObjectPropertyFromString(
                                                                                          row,
                                                                                          column.field
                                                                                        ),
                                                                                        column.charLimit,
                                                                                        column.numeric ? 'number' : 'text'
                                                                                      )}
                                                                                    onChange={setMultiEdit
                                                                                      ? null
                                                                                      : (event) =>
                                                                                          changeTextValue(
                                                                                            event.target.value,
                                                                                            column,
                                                                                            columnIndex,
                                                                                            row
                                                                                          )}
                                                                                  />
                                                                                  )
                                                                            )
                                                                        )
                                                                    )
                                                )
                                              : ((getObjectPropertyFromString(
                                                  row,
                                                  column.field
                                                ) ||
                                            (getObjectPropertyFromString(
                                              row,
                                              column.field
                                            ) === 0)) && getColumnType(column, row) !== 'smartMultiSelect' && getColumnType(column, row) !== 'boolean')
                                                  ? <Typography color={typeof column.color === 'function' ? column.color(row) : column.color ? column.color : undefined}>{cellText(row, column)}</Typography>
                                                  : (getColumnType(column, row) === 'smartMultiSelect'
                                                      ? (
                                                        <SmartSelect
                                                          readOnly
                                                          multiple
                                                          width={column.width}
                                                          value={selectedValuesMultiSelect(row, column, true)}
                                                          disabledOptions={getDisabledOpts(row, column)}
                                                          sortValues={column.sortValues}
                                                          optionLabelTransform={(value) => readOnlyChipLabel(row, column, value)}
                                                          iconFunction={column.iconFunction}
                                                          sourceWithSearch={column.edit?.sourceWithSearch ? async (search) => await sourceWithSearch(row, column, search) : undefined}
                                                        />
                                                        )
                                                      : getColumnType(column, row) === 'boolean'
                                                        ? getBooleanType(row, column.field, true, false)
                                                        : '')
                                      )
                                    : null}
                                </Box>
                              </TableCell>
                              )
                            : null
                        })}
                        {/* Actions columns */}
                        {actions && (
                          (actions.edit && (actions.edit.fetchFunction || actions.edit.redirect)) ||
                            (actions.custom && actions.custom.length > 0) ||
                            actions.accordion ||
                            actions.delete
                        )
                          ? (
                            <InputLabel />,
                              <TableCell
                                key={'action' + rowIndex}
                                sx={{ ...tableCellStyle, position: 'sticky', right: 0, zIndex: 2 }}
                              >
                                <Stack
                                  direction='row'
                                  justifyContent='center'
                                  alignItems='center'
                                >
                                  {/* If a record is been editing, show close and save icons instead edit */}
                                  {rowHasTableIndexDefined(row) && editIndex === row[smartTableIndexKey]
                                    ? (
                                      <>
                                        <IconButton
                                          onClick={() => {
                                            showAlert({
                                              title: t_('There have not been modifications.'),
                                              severity: 'info'
                                            })
                                            if (editIndex === -1) {
                                              const previous = page * rowsPerPage
                                              setData((previousData) => [
                                                ...previousData.slice(0, previous),
                                                ...previousData.slice(previous + 1)
                                              ])
                                              setTimeout(() => setSelectedRow(-1), 120)
                                            } else {
                                              setData((previous) =>
                                                previous.map((previousRow) =>
                                                  rowHasTableIndexDefined(previousRow) && previousRow[smartTableIndexKey] ===
                                                      editIndex
                                                    ? notSavedEditingRow
                                                    : previousRow
                                                )
                                              )
                                            }
                                            setEditIndex(-2)
                                            if (onEditIndexChanged) {
                                              onEditIndexChanged(-2)
                                            }
                                            setErrorTextfield(handleColumnsErrors)
                                            if (editingRow !== undefined) editingRow(false)
                                            setLoadTable(!loadTable)
                                            if (setRowCreatedInOtherSmartTable !== null && setRowCreatedInOtherSmartTable !== undefined) {
                                              setRowCreatedInOtherSmartTable(null)
                                            }
                                          }}
                                        >
                                          <Close />
                                        </IconButton>
                                        {/* Save button */}
                                        <IconButton
                                          disabled={disableSaveButton()}
                                          onClick={() => {
                                            const error = validateColumError(row, columns)
                                            if (error || disableSave) return
                                            if (editIndex === -1) {
                                              if (
                                                typeof actions.create
                                                  .fetchFunction === 'function'
                                              ) {
                                                setEditIndex(-2)
                                                if (onEditIndexChanged) {
                                                  onEditIndexChanged(-2)
                                                }
                                                const saveFetch = (elem) => {
                                                  actions.create
                                                    .fetchFunction(elem)
                                                    .then((data) => {
                                                      if (handleRowCreatedInOtherSmartTable !== undefined) handleRowCreatedInOtherSmartTable(data)
                                                      resetPageCount()
                                                      showAlert({
                                                        title: t_('Created correctly.'),
                                                        severity: 'success'
                                                      })
                                                      setSelectedItem(elem)
                                                    })
                                                    .catch((error) => {
                                                      showAlert({
                                                        title:
                                                            '',
                                                        message: error.message,
                                                        severity: error.name
                                                      })
                                                    })
                                                  setLoadTable(!loadTable)
                                                }
                                                if (actions.create.validate) {
                                                  setOpenCreateDialog(true)
                                                  setCurrentSelectedRow(row)
                                                } else {
                                                  saveFetch(row)
                                                }
                                              }
                                            } else {
                                              if (
                                                typeof actions.edit.fetchFunction ===
                                                  'function'
                                              ) {
                                                if (editingRow !== undefined) editingRow(false)
                                                if (actions.edit.validate) {
                                                  setOpenEditDialog(true)
                                                  setCurrentSelectedRow(true)
                                                } else {
                                                  saveFetch(row)
                                                }
                                                setEditIndex(-2)
                                                if (onEditIndexChanged) {
                                                  onEditIndexChanged(-2)
                                                }
                                                setLoadTable(!loadTable)
                                              }
                                            }
                                          }}
                                        >
                                          <Save />
                                        </IconButton>
                                      </>
                                      )
                                    : actions
                                      ? (
                                        <Stack
                                          direction='row'
                                          justifyContent='center'
                                          alignItems='center'
                                        >
                                          {actions?.accordion && (!actions?.accordion?.hide || !actions?.accordion.hide(row))
                                            ? (
                                              <Tooltip title={typeof actions?.accordion?.tooltip === 'function' ? actions.accordion.tooltip(row) : actions?.accordion?.tooltip || ''}>
                                                <span>
                                                  <IconButton disabled={actions?.accordion.disabled} onClick={(event) => { getAccordionVisibility().toggleVisibility(row.id) }} style={{ visibility: (actions.accordion.visibilityButton !== undefined ? (!actions.accordion.visibilityButton(row) ? 'hidden' : 'visible') : 'visible') }}>
                                                    {getAccordionVisibility().getSmartAccordionVisibility(row.id)
                                                      ? <ArrowUpwardOutlined />
                                                      : <ArrowDownwardOutlined />}
                                                  </IconButton>
                                                </span>
                                              </Tooltip>
                                              )
                                            : null}
                                          {actions?.custom?.map((action, index) => {
                                            if (action.visible === false) return null
                                            if (action.contextMenu === false && action.render) {
                                              const content = action.render(row, dataFetch, requestPagination)
                                              return content ? <div key={'direct-icon-' + index}>{content}</div> : null
                                            }
                                            return null
                                          })}
                                          {(actions?.custom?.some(action => action.contextMenu !== false) ||
                                            actions?.edit !== undefined ||
                                            actions?.delete !== undefined) &&
                                            (
                                              <ActionsMenu
                                                row={row}
                                                actions={{
                                                  ...actions,
                                                  custom: actions?.custom
                                                    ?.filter(action =>
                                                      action.contextMenu !== false &&
                                                      action.visible !== false &&
                                                      !action.hide?.(row)
                                                    )
                                                }}
                                                editIndex={editIndex}
                                                setEditIndex={setEditIndex}
                                                onEditIndexChanged={onEditIndexChanged}
                                                editingRow={editingRow}
                                                getAccordionVisibility={getAccordionVisibility}
                                                dataFetch={dataFetch}
                                                requestPagination={requestPagination}
                                                smartTableIndexKey={smartTableIndexKey}
                                                multipleSelectionValue={multipleSelectionValue}
                                                setOpenDeleteDialog={setOpenDeleteDialog}
                                                setCurrentSelectedRow={setCurrentSelectedRow}
                                                setDisableRowActions={setDisableRowActions}
                                                t_={t_}
                                                setNotSavedEditingRow={setNotSavedEditingRow}
                                                rowHasTableIndexDefined={rowHasTableIndexDefined}
                                              />)}
                                        </Stack>)
                                      : null}
                                </Stack>
                              </TableCell>
                            )
                          : null}
                      </SmartTableRow>
                      {actions?.accordion ? <SmartAccordion visible={getAccordionVisibility().getSmartAccordionVisibility(row.id)} renderComponent={actions.accordion.render(row)} colSpan={columnsForRender.length + 1 + (multipleSelection ? 1 : 0)} /> : null}
                      {secondaryLineColumns.map((column) => {
                        const content = column.render
                          ? column.render(row, { showAlert })
                          : getObjectPropertyFromString(row, column.field)
                        if (content === null || content === undefined || content === '') {
                          return null
                        }
                        return (
                          <SmartTableRow
                            row={rowIndex % 2 !== 0}
                            key={'secondary' + rowIndex + '_' + column.field}
                            groupHover
                            backgroundColor={rowBackgroundColorFunction && typeof rowBackgroundColorFunction === 'function' ? rowBackgroundColorFunction(row) : null}
                            onMouseEnter={() => setHoveredRecordIndex(rowIndex)}
                            onMouseLeave={() => setHoveredRecordIndex(-1)}
                            selected={selectedRow === rowIndex || hoveredRecordIndex === rowIndex}
                          >
                            <TableCell
                              colSpan={columnsForRender.length + (multipleSelection ? 1 : 0) + (actions ? 1 : 0)}
                              sx={{ ...tableCellStyle, borderTop: 'none', ...(column.secondaryLineCellStyle || {}) }}
                            >
                              {content}
                            </TableCell>
                          </SmartTableRow>
                        )
                      })}
                    </React.Fragment>
                  ))}
                  {dataAvailable.current === false && firstLoad
                    ? <TableRow><TableCell align='center' sx={{ paddingTop: '2rem', paddingBottom: '2rem' }} colSpan={multipleSelection ? columnsForRender.length + 1 : columnsForRender.length}><Typography color='primary' fontSize='1.5rem'>{t_(noDataMessage)}</Typography></TableCell></TableRow>
                    : null}
                </TableBody>

                {tablePageSize > 0 && tablePageSize !== Number.MAX_SAFE_INTEGER && (tableTuneUniqueKey === '' || rowsPerPagePreferenceLoaded)
                  ? (
                    <TableFooter>
                      <TableRow>
                        <SmartMateriaUITablePagination
                          pageable={pageable}
                          searchAndPaginationVisible={searchAndPaginationVisible}
                          setPage={setPage}
                          setRowsPerPage={setRowsPerPage}
                          onRowsPerPageChange={handleRowsPerPagePreferenceChanged}
                          pageableObject={pageableObject}
                          setPageableObject={setPageableObject}
                          editIndex={editIndex}
                          getTableDataFiltered={getTableDataFilteredFromData}
                          page={page}
                          rowsPerPage={rowsPerPage}
                          loadTable={loadTable}
                          setLoadTable={setLoadTable}
                          rowsPerPageOptions={rowsPerPageOptions}
                        />
                      </TableRow>
                    </TableFooter>
                    )
                  : null}
              </Table>
              )
            : (
              <Typography
                variant='h6'
                component='div'
              >
                NO COLUMNS DEFINED
              </Typography>
              )}
        </TableContainer>
      </div>
    </SmartTablePaper>
  )
}
export default SmartMateriaUITable
